import { useState, useEffect, useCallback, useMemo } from "react";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { useAuth } from "@/components/auth/AuthContext";
import { apiFetch } from "@/lib/apiFetch";
import { format, formatDistanceToNow, differenceInCalendarDays } from "date-fns";
import {
  Shield, Loader2, AlertTriangle, Siren, ClipboardList, MapPin, Calendar, UserMinus2,
  Star, FileText, UserCheck, ChevronDown, ChevronUp, CheckCircle2, XCircle,
  Award, PlusCircle, Clock, ThumbsUp, ThumbsDown, ExternalLink, Send, TrendingUp,
  GripVertical, Rows3, Save, RefreshCw, Info, Crown, Radio, Activity, Crosshair, Users,
  Package, Server, Copy, Eye, EyeOff, CheckSquare, Square, Lock, ChevronLeft, Plus, Trash2, Pencil, UserCircle2, GraduationCap, Brain, Target,
  BookOpen, Layers, Flag, BrainCircuit, ShieldAlert, ClipboardCheck, ScrollText, MessageSquare,
  PhoneCall, Antenna
} from "lucide-react";
import { Link } from "wouter";
import { getRibbonModifiers } from '@/lib/ribbonModifiers';
import { normaliseConductReports, normaliseCampaigns, normaliseBriefings } from '@/lib/normaliseApi';

/* ─── helpers ─────────────────────────────────────────────────────────────── */
const CL: Record<string, string> = {
  unclassified: "text-green-400 border-green-500/30 bg-green-500/10",
  confidential: "text-blue-400 border-blue-500/30 bg-blue-500/10",
  classified:   "text-yellow-400 border-yellow-500/30 bg-yellow-500/10",
  "top-secret": "text-red-400 border-red-500/30 bg-red-500/10",
};
const SC: Record<string, string> = {
  draft:     "text-muted-foreground border-border",
  published: "text-primary border-primary/30 bg-primary/10",
  archived:  "text-muted-foreground border-border",
};
const STATUS_OP: Record<string, string> = {
  Planned:   "text-amber-400 bg-amber-500/10 border-amber-500/30",
  Confirmed: "text-green-400 bg-green-500/10 border-green-500/30",
  Active:    "text-red-400 bg-red-500/10 border-red-500/30",
  Completed: "text-muted-foreground bg-secondary border-border",
  Cancelled: "text-muted-foreground bg-secondary/30 border-border",
};

/* ─── main component ──────────────────────────────────────────────────────── */
export default function MemberHQ() {
  const { user } = useAuth();
  const [tab, setTab] = useState<"reports"|"reviews"|"servers"|"personal-admin">("reports");
  const [personalAdminSub, setPersonalAdminSub] = useState<"service-file"|"ribbon-bar"|"loa"|"loadouts"|"discharge-papers">("service-file");
  const [reportSub, setReportSub] = useState<"ops"|"warnos"|"sitreps"|"aars">("ops");
  const [intelSub, setIntelSub] = useState<"briefings"|"intel"|"campaigns"|"duty-roster"|"orders"|"op-plans"|"comms-plans">("orders");
  const [unitSub, setUnitSub] = useState<"conduct"|"training-docs">("conduct");
  const [reviewSub, setReviewSub] = useState<"peer-review"|"unit-review"|"training-reviews">("peer-review");
  const [upvoteCount, setUpvoteCount] = useState<number>(0);
  const [hasVoted, setHasVoted] = useState<boolean>(false);
  const [upvoting, setUpvoting] = useState(false);
  const [memberships, setMemberships] = useState<any[] | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);
  const [rosterEntry, setRosterEntry] = useState<any | null>(null);
  const [groupDetail, setGroupDetail] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ok:boolean;text:string}|null>(null);
  const [isPro, setIsPro] = useState<boolean | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fetchRetry, setFetchRetry] = useState(0);

  const showMsg = (ok: boolean, text: string) => { setMsg({ok,text}); setTimeout(()=>setMsg(null),3500); };

  // Single combined fetch — memberships + full group detail + pro status in parallel.
  // Result is cached in sessionStorage so navigating away and back is instant.
  useEffect(() => {
    if (!user?.id) return;
    const CACHE_KEY = `memberhq_cache_${user.id}`;
    // Serve cached data immediately (no loading flash)
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const { groups, detail, entry, pro } = JSON.parse(cached);
        setMemberships(groups);
        setSelectedGroup(groups?.[0] ?? null);
        setGroupDetail(detail);
        setRosterEntry(entry);
        setIsPro(!!pro);
        setLoading(false); // instant render from cache
      }
    } catch {}
    setFetchError(null);
    // Always re-fetch in background to stay fresh
    apiFetch<any[]>("/milsimGroups?path=mine/memberships")
      .then(async (groups) => {
        const safeGroups = groups ?? [];
        setMemberships(safeGroups);
        const first = safeGroups[0] ?? null;
        setSelectedGroup(first);
        if (!first) { setLoading(false); return; }
        // Fetch group detail + pro status in parallel
        const [detail, proData] = await Promise.all([
          apiFetch<any>(`/milsimGroups?path=${first.id}/full`).catch(() => null),
          apiFetch<any>(`/getProStatus?path=status&group_id=${first.id}`).catch(() => null),
        ]);
        const entry = detail ? (detail.roster ?? []).find((r: any) => r.userId === (user as any).id || r.user_id === (user as any).id) ?? null : null;
        setGroupDetail(detail);
        setRosterEntry(entry);
        setIsPro(!!proData?.is_pro);
        // Cache for instant re-render next time
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ groups: safeGroups, detail, entry, pro: !!proData?.is_pro })); } catch {}
        setLoading(false);
      })
      .catch((err) => {
        console.error('[MemberHQ] fetch failed:', err);
        // Only show error if we have nothing cached to show
        setMemberships(prev => { if (prev !== null) return prev; setFetchError(err?.message ?? 'Failed to load unit data.'); return null; });
        setLoading(false);
      });
  }, [user?.id, fetchRetry]);

  useEffect(() => {
    if (!selectedGroup) return;
    apiFetch<{ count: number; voted: boolean }>(`/groupUpvotes?path=upvotes/${selectedGroup.id}`, { method: "GET" })
      .then(d => { setUpvoteCount(d.count ?? 0); setHasVoted(d.voted ?? false); })
      .catch(() => {});
  }, [selectedGroup]);

  const handleUpvote = async () => {
    if (!selectedGroup || upvoting) return;
    setUpvoting(true);
    try {
      const res = await apiFetch<{ count: number; voted: boolean }>(`/groupUpvotes?path=upvotes/${selectedGroup.id}`, { method: "POST" });
      setUpvoteCount(res.count ?? 0);
      setHasVoted(res.voted ?? false);
    } catch {}
    setUpvoting(false);
  };

  if (loading) return (
    <PortalLayout>
      <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </PortalLayout>
  );

  if (fetchError) return (
    <PortalLayout>
      <div className="text-center py-24 border border-dashed border-red-500/40 rounded-lg max-w-xl mx-auto mt-12">
        <Shield className="w-14 h-14 text-red-400 mx-auto mb-4 opacity-40" />
        <h2 className="font-display font-black text-xl uppercase tracking-wider mb-2 text-red-400">Connection Error</h2>
        <p className="text-muted-foreground font-sans text-sm mb-6">{fetchError}</p>
        <button
          onClick={() => { setFetchRetry(r => r + 1); }}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-widest text-sm px-6 py-3 rounded transition-all"
        >
          Retry
        </button>
      </div>
    </PortalLayout>
  );

  if (memberships !== null && memberships.length === 0) return (
    <PortalLayout>
      <div className="text-center py-24 border border-dashed border-border rounded-lg max-w-xl mx-auto mt-12">
        <Shield className="w-14 h-14 text-muted-foreground mx-auto mb-4 opacity-20" />
        <h2 className="font-display font-black text-xl uppercase tracking-wider mb-2">Not Enlisted</h2>
        <p className="text-muted-foreground font-sans text-sm mb-6">You're not on the roster of any MilSim unit. Find a unit and submit an application to join.</p>
        <Link href="/milsim">
          <a className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-widest text-sm px-6 py-3 rounded transition-all">
            Browse Units
          </a>
        </Link>
      </div>
    </PortalLayout>
  );

  const TABS = [
    { id: "reports",        label: "Reports",        icon: ClipboardList },
    { id: "intel",          label: "Intel & Orders", icon: BrainCircuit },
    { id: "unit-records",   label: "Unit Records",   icon: ScrollText },
    { id: "reviews",        label: "Reviews",        icon: Star },
    { id: "servers",        label: "Game Servers",   icon: Server },
    { id: "personal-admin", label: "Personal Admin", icon: UserCircle2 },
  ] as const;

  const PERSONAL_ADMIN_SUBS = [
    { id: "service-file",      label: "Service File",    icon: FileText },
    { id: "ribbon-bar",        label: "Ribbon Bar",      icon: Rows3, pro: true },
    { id: "loa",               label: "Request LOA",     icon: Calendar },
    { id: "loadouts",          label: "Loadout Kits",    icon: Package },
    { id: "discharge-papers",  label: "Discharge Papers",icon: UserMinus2 },
    { id: "qualifications",    label: "Qualifications",  icon: GraduationCap },
  ] as const;

  const REPORT_SUBS = [
    { id: "ops",     label: "Live Ops", icon: Siren },
    { id: "warnos",  label: "WARNOs",   icon: Radio },
    { id: "sitreps", label: "SITREPs",  icon: Activity },
    { id: "aars",    label: "AARs",     icon: ClipboardList },
  ] as const;

  const REVIEW_SUBS = [
    { id: "peer-review",      label: "Peer Review",      icon: Star },
    { id: "unit-review",      label: "Unit Review",      icon: Shield },
    { id: "training-reviews", label: "Training Reviews", icon: Brain },
  ] as const;

  const INTEL_SUBS = [
    { id: "orders",       label: "Orders & Memos", icon: ScrollText },
    { id: "briefings",    label: "Briefings",      icon: FileText },
    { id: "op-plans",     label: "Op Plans",       icon: Layers },
    { id: "comms-plans",  label: "Comms Plans",    icon: PhoneCall },
    { id: "intel",        label: "Op Intel",       icon: BrainCircuit },
    { id: "campaigns",    label: "Campaigns",      icon: Flag },
    { id: "duty-roster",  label: "Duty Roster",    icon: ClipboardCheck },
  ] as const;

  const UNIT_RECORD_SUBS = [
    { id: "conduct",        label: "Conduct Reports", icon: ShieldAlert },
    { id: "training-docs",  label: "Training Library",icon: BookOpen },
  ] as const;

  return (
    <PortalLayout>
      {/* Header */}
      <div className="mb-6 pb-6 border-b border-border">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/20 text-primary rounded flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-display font-black text-2xl uppercase tracking-widest">Member HQ</h1>
              <p className="text-xs text-muted-foreground font-sans">Your unit portal — everything from your commander's perspective</p>
            </div>
          </div>
          {/* Group selector if in multiple units */}
          {memberships.length > 1 && (
            <select
              value={selectedGroup?.id ?? ""}
              onChange={async e => {
                const g = memberships.find(m => m.id === e.target.value);
                if (!g || !user) return;
                setSelectedGroup(g);
                setGroupDetail(null);
                setRosterEntry(null);
                const [detail, proData] = await Promise.all([
                  apiFetch<any>(`/milsimGroups?path=${g.id}/full`).catch(() => null),
                  apiFetch<any>(`/getProStatus?path=status&group_id=${g.id}`).catch(() => null),
                ]);
                const entry = detail ? (detail.roster ?? []).find((r: any) => r.userId === (user as any).id || r.user_id === (user as any).id) ?? null : null;
                setGroupDetail(detail);
                setRosterEntry(entry);
                setIsPro(!!proData?.is_pro);
              }}
              className="bg-background border border-border rounded px-3 py-2 text-sm font-display uppercase tracking-widest"
            >
              {memberships.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          )}
          {memberships.length === 1 && (
            <Link href={`/milsim/${selectedGroup?.slug}`}>
              <a className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary font-display uppercase tracking-widest transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> {selectedGroup?.name}
              </a>
            </Link>
          )}
          {/* Upvote button */}
          {selectedGroup && (
            <button
              onClick={handleUpvote}
              disabled={upvoting}
              title={hasVoted ? "Click to remove your upvote" : "Upvote your unit to boost its ranking on the registry"}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${
                hasVoted
                  ? "bg-primary/15 border-primary/50 text-primary"
                  : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
              }`}>
              {upvoting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
              <span>{upvoteCount}</span>
              <span>{hasVoted ? "Upvoted" : "Upvote Unit"}</span>
            </button>
          )}
        </div>

        {/* Roster badge */}
        {rosterEntry && (
          <div className="flex items-center gap-3 mt-4 p-3 bg-secondary/40 border border-border rounded-lg flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground font-display uppercase tracking-widest">Callsign</span>
              <span className="font-display font-black text-sm text-foreground">{rosterEntry.callsign}</span>
            </div>
            {rosterEntry.rankName && <span className="text-xs text-muted-foreground">· {rosterEntry.rankName}</span>}
            {rosterEntry.roleName && <span className="text-xs text-muted-foreground">· {rosterEntry.roleName}</span>}
            <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ml-auto ${rosterEntry.status === 'active' ? 'text-green-400 bg-green-500/10 border-green-500/30' : 'text-amber-400 bg-amber-500/10 border-amber-500/30'}`}>
              {rosterEntry.status ?? "active"}
            </span>
          </div>
        )}
      </div>

      {/* Flash message */}
      {msg && (
        <div className={`flex items-center gap-3 p-3 rounded-lg border text-sm font-sans mb-4 ${msg.ok ? "bg-primary/10 border-primary/30 text-primary" : "bg-destructive/10 border-destructive/30 text-destructive"}`}>
          {msg.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
          {msg.text}
        </div>
      )}

      {/* Sidebar + content layout */}
      <div className="flex gap-0 min-h-[600px]">
        {/* Left sidebar */}
        <div className="shrink-0 w-44 border-r border-border/60 pr-2 space-y-0.5 pt-1">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id as any)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded text-left font-display font-bold uppercase tracking-widest text-[10px] transition-colors ${
                  tab === t.id
                    ? "bg-primary/15 text-primary border-l-2 border-primary pl-2.5"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`}>
                <Icon className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content panel */}
        <div className="flex-1 pl-5 min-w-0">
          {selectedGroup && tab === "reports" && (
            <div>
              {/* Sub-tab bar */}
              <div className="flex gap-1.5 mb-5 flex-wrap">
                {REPORT_SUBS.map(s => {
                  const Icon = s.icon;
                  return (
                    <button key={s.id} onClick={() => setReportSub(s.id as any)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-[10px] font-display font-bold uppercase tracking-widest transition-all ${
                        reportSub === s.id
                          ? "bg-primary/15 text-primary border-primary/40"
                          : "bg-secondary border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                      }`}>
                      <Icon className="w-3.5 h-3.5" />
                      {s.label}
                    </button>
                  );
                })}
              </div>
              {reportSub === "ops"     && <MemberOpsTab     group={selectedGroup} showMsg={showMsg} rosterEntry={rosterEntry} user={user} />}
              {reportSub === "warnos"  && <MemberWarnosTab  group={selectedGroup} showMsg={showMsg} />}
              {reportSub === "sitreps" && <MemberSitrepsTab group={selectedGroup} showMsg={showMsg} rosterEntry={rosterEntry} />}
              {reportSub === "aars"    && <MemberAARsTab    group={selectedGroup} showMsg={showMsg} rosterEntry={rosterEntry} />}
            </div>
          )}
          {selectedGroup && tab === "intel" && (
            <div>
              <div className="flex gap-1.5 mb-5 flex-wrap">
                {INTEL_SUBS.map(s => {
                  const Icon = s.icon;
                  return (
                    <button key={s.id} onClick={() => setIntelSub(s.id as any)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-[10px] font-display font-bold uppercase tracking-widest transition-all ${
                        intelSub === s.id
                          ? "bg-primary/15 text-primary border-primary/40"
                          : "bg-secondary border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                      }`}>
                      <Icon className="w-3.5 h-3.5" />
                      {s.label}
                    </button>
                  );
                })}
              </div>
              {intelSub === "orders"      && <MemberOrdersInbox    group={selectedGroup} user={user} rosterEntry={rosterEntry} />}
              {intelSub === "briefings"   && <MemberBriefingsTab   group={selectedGroup} showMsg={showMsg} />}
              {intelSub === "op-plans"    && <MemberOpPlansTab     group={selectedGroup} />}
              {intelSub === "comms-plans" && <MemberCommsPlansTab  group={selectedGroup} />}
              {intelSub === "intel"       && <MemberOpIntelTab     group={selectedGroup} showMsg={showMsg} />}
              {intelSub === "campaigns"   && <MemberCampaignsTab   group={selectedGroup} />}
              {intelSub === "duty-roster" && <MemberDutyRosterTab  group={selectedGroup} user={user} rosterEntry={rosterEntry} />}
            </div>
          )}
          {selectedGroup && tab === "unit-records" && (
            <div>
              <div className="flex gap-1.5 mb-5 flex-wrap">
                {UNIT_RECORD_SUBS.map(s => {
                  const Icon = s.icon;
                  return (
                    <button key={s.id} onClick={() => setUnitSub(s.id as any)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-[10px] font-display font-bold uppercase tracking-widest transition-all ${
                        unitSub === s.id
                          ? "bg-primary/15 text-primary border-primary/40"
                          : "bg-secondary border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                      }`}>
                      <Icon className="w-3.5 h-3.5" />
                      {s.label}
                    </button>
                  );
                })}
              </div>
              {unitSub === "conduct"       && <MemberConductTab       group={selectedGroup} user={user} rosterEntry={rosterEntry} />}
              {unitSub === "training-docs" && <MemberTrainingLibTab   group={selectedGroup} />}
            </div>
          )}
          {selectedGroup && tab === "servers"      && <MemberServersTab      group={selectedGroup} />}
          {selectedGroup && tab === "reviews" && (
            <div>
              <div className="flex gap-1.5 mb-5 flex-wrap">
                {REVIEW_SUBS.map(s => {
                  const Icon = s.icon;
                  return (
                    <button key={s.id} onClick={() => setReviewSub(s.id as any)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-[10px] font-display font-bold uppercase tracking-widest transition-all ${
                        reviewSub === s.id
                          ? "bg-primary/15 text-primary border-primary/40"
                          : "bg-secondary border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                      }`}>
                      <Icon className="w-3.5 h-3.5" />
                      {s.label}
                    </button>
                  );
                })}
              </div>
              {reviewSub === "peer-review"      && <MemberPeerReviewTab      group={selectedGroup} showMsg={showMsg} user={user} />}
              {reviewSub === "unit-review"      && <MemberUnitReviewTab      group={selectedGroup} showMsg={showMsg} user={user} />}
              {reviewSub === "training-reviews" && <MemberTrainingReviewsTab group={selectedGroup} showMsg={showMsg} user={user} />}
            </div>
          )}
          {selectedGroup && tab === "personal-admin" && (
            <div>
              {/* Personal Admin sub-tabs */}
              <div className="flex gap-1.5 mb-5 flex-wrap">
                {PERSONAL_ADMIN_SUBS.map(s => {
                  const Icon = s.icon;
                  return (
                    <button key={s.id} onClick={() => setPersonalAdminSub(s.id as any)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-[10px] font-display font-bold uppercase tracking-widest transition-all ${
                        personalAdminSub === s.id
                          ? "bg-primary/15 text-primary border-primary/40"
                          : "bg-secondary border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                      }`}>
                      <Icon className="w-3.5 h-3.5" />
                      {s.label}
                      {(s as any).pro && <Crown className="w-2.5 h-2.5 text-yellow-400 ml-0.5 shrink-0" />}
                    </button>
                  );
                })}
              </div>
              {personalAdminSub === "service-file"     && <MemberServiceFileTab  user={user} rosterEntry={rosterEntry} groupDetail={groupDetail} />}
              {personalAdminSub === "ribbon-bar"       && <MemberRibbonBarTab    group={selectedGroup} user={user} rosterEntry={rosterEntry} isPro={isPro} />}
              {personalAdminSub === "loa"              && <MemberLOATab          group={selectedGroup} showMsg={showMsg} user={user} rosterEntry={rosterEntry} />}
              {personalAdminSub === "loadouts"         && <MemberLoadoutsTab     group={selectedGroup} groupDetail={groupDetail} rosterEntry={rosterEntry} />}
              {personalAdminSub === "discharge-papers" && <MemberDischargeTab    group={selectedGroup} user={user} rosterEntry={rosterEntry} groupDetail={groupDetail} />}
              {personalAdminSub === "qualifications"    && <MemberQualificationsTab rosterEntry={rosterEntry} groupDetail={groupDetail} />}
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}

/* ─── Ops tab ─────────────────────────────────────────────────────────────── */
function MemberOpsTab({ group, showMsg, rosterEntry, user }: any) {
  const [ops, setOps] = useState<any[]>([]);
  const [rsvps, setRsvps] = useState<Record<string, string>>({}); // op_id → status
  const [rsvping, setRsvping] = useState<string|null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string|null>(null);

  const loadOps = useCallback(() => {
    apiFetch<any>(`/activityCalendar?path=list&group_id=${group.id}`)
      .then((r: any) => setOps((r.events ?? []).sort((a: any, b: any) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime())))
      .catch(() => setOps([]))
      .finally(() => setLoading(false));
  }, [group.id]);

  const loadRsvps = useCallback(() => {
    if (!user?.id) return;
    apiFetch<any>(`/milsimOps?path=my-rsvps&group_id=${group.id}`)
      .then((r: any) => {
        const map: Record<string, string> = {};
        (r.rsvps ?? []).forEach((rv: any) => { map[rv.event_id ?? rv.op_id] = rv.status; });
        setRsvps(map);
      })
      .catch(() => {});
  }, [group.id, user?.id]);

  useEffect(() => { loadOps(); loadRsvps(); }, [loadOps, loadRsvps]);

  const handleRsvp = async (opId: string, status: "attending" | "declined") => {
    setRsvping(opId + status);
    try {
      await apiFetch(`/milsimOps?path=rsvp`, {
        method: "POST",
        body: JSON.stringify({ op_id: opId, group_id: group.id, status }),
      });
      setRsvps(prev => ({ ...prev, [opId]: status }));
      showMsg(true, status === "attending" ? "RSVP confirmed — you're on the manifest." : "RSVP declined.");
    } catch {
      showMsg(false, "Failed to submit RSVP — try again.");
    } finally {
      setRsvping(null);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (ops.length === 0) return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg">
      <Siren className="w-10 h-10 mx-auto mb-3 opacity-20" />
      <p className="font-display text-sm uppercase tracking-widest text-muted-foreground">No operations on record</p>
      <p className="text-xs text-muted-foreground font-sans mt-1">Your commander will schedule ops here</p>
    </div>
  );

  return (
    <div className="space-y-3 max-w-3xl">
      <p className="text-xs text-muted-foreground font-sans">{ops.length} operation{ops.length !== 1 ? "s" : ""} on record</p>
      {ops.map((op: any) => {
        const myRsvp = rsvps[op.id];
        const isUpcoming = op.status !== "Completed" && op.status !== "Cancelled";
        return (
          <div key={op.id} className="bg-card border border-border rounded-lg overflow-hidden">
            <button onClick={() => setExpanded(expanded === op.id ? null : op.id)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-secondary/20 transition-colors text-left">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${STATUS_OP[op.status ?? "Planned"] ?? ""}`}>{op.status ?? "Planned"}</span>
                <span className="font-display font-bold text-sm">{op.name ?? op.title}</span>
                {op.game && <span className="text-xs text-muted-foreground">{op.game}</span>}
                {myRsvp === "attending" && <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border text-green-400 border-green-500/30 bg-green-500/10">✓ Attending</span>}
                {myRsvp === "declined"  && <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border text-red-400 border-red-500/30 bg-red-500/10">✗ Declined</span>}
              </div>
              <div className="flex items-center gap-2 shrink-0 text-muted-foreground">
                {op.scheduled_at && <span className="text-xs font-sans">{format(new Date(op.scheduled_at), "dd MMM yyyy")}</span>}
                {expanded === op.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </button>
            {expanded === op.id && (
              <div className="border-t border-border p-5 bg-secondary/10 space-y-4">
                {op.description && <p className="text-sm text-muted-foreground font-sans leading-relaxed">{op.description}</p>}
                <div className="grid grid-cols-2 gap-3 text-xs font-sans">
                  {op.event_type  && <div><span className="text-muted-foreground">Type: </span><span className="text-foreground">{op.event_type}</span></div>}
                  {op.scheduled_at && <div><span className="text-muted-foreground">Scheduled: </span><span className="text-foreground">{format(new Date(op.scheduled_at), "dd MMM yyyy HH:mm")}</span></div>}
                  {op.end_date     && <div><span className="text-muted-foreground">End: </span><span className="text-foreground">{format(new Date(op.end_date), "dd MMM yyyy")}</span></div>}
                </div>
                {isUpcoming && (
                  <div className="flex items-center gap-2 pt-1">
                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mr-1">RSVP:</p>
                    <button
                      disabled={!!rsvping}
                      onClick={() => handleRsvp(op.id, "attending")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${
                        myRsvp === "attending"
                          ? "bg-green-500/15 border-green-500/50 text-green-400"
                          : "border-border text-muted-foreground hover:border-green-500/40 hover:text-green-400"
                      }`}>
                      {rsvping === op.id + "attending" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                      Attending
                    </button>
                    <button
                      disabled={!!rsvping}
                      onClick={() => handleRsvp(op.id, "declined")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${
                        myRsvp === "declined"
                          ? "bg-red-500/15 border-red-500/50 text-red-400"
                          : "border-border text-muted-foreground hover:border-red-500/40 hover:text-red-400"
                      }`}>
                      {rsvping === op.id + "declined" ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                      Decline
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Shared field renderer ──────────────────────────────────────────────── */
function BriefField({ label, value }: any) {
  return (
    <div>
      <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
      <p className="text-sm font-sans whitespace-pre-wrap text-foreground">{value}</p>
    </div>
  );
}

/* ─── WARNOs tab ──────────────────────────────────────────────────────────── */
function MemberWarnosTab({ group, showMsg }: any) {
  const [warnos, setWarnos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string|null>(null);

  useEffect(() => {
    apiFetch<any>(`/milsimGroupsB?path=${group.id}/warnos`)
      .then((r: any) => setWarnos((Array.isArray(r) ? r : (r.warnos ?? [])).sort((a: any, b: any) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime())))
      .catch(() => setWarnos([]))
      .finally(() => setLoading(false));
  }, [group.id]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (warnos.length === 0) return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg">
      <Radio className="w-10 h-10 mx-auto mb-3 opacity-20" />
      <p className="font-display text-sm uppercase tracking-widest text-muted-foreground">No WARNOs issued</p>
      <p className="text-xs text-muted-foreground font-sans mt-1">Warning orders from your commander will appear here</p>
    </div>
  );

  return (
    <div className="space-y-3 max-w-3xl">
      <p className="text-xs text-muted-foreground font-sans">{warnos.length} warning order{warnos.length !== 1 ? "s" : ""}</p>
      {warnos.map((w: any) => (
        <div key={w.id} className="bg-card border border-border rounded-lg overflow-hidden">
          <button onClick={() => setExpanded(expanded === w.id ? null : w.id)}
            className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-secondary/20 transition-colors text-left">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
                w.status === "issued" ? "text-yellow-400 border-yellow-500/30 bg-yellow-500/10" :
                w.status === "acknowledged" ? "text-green-400 border-green-500/30 bg-green-500/10" :
                "text-muted-foreground border-border"
              }`}>{w.status ?? "draft"}</span>
              <span className="font-display font-bold text-sm">{w.title}</span>
              {w.op_date && <span className="text-xs text-muted-foreground">{format(new Date(w.op_date + "T00:00:00"), "dd MMM yyyy")}</span>}
            </div>
            {expanded === w.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>
          {expanded === w.id && (
            <div className="border-t border-border p-5 bg-secondary/10 space-y-4">
              {w.situation_ground   && <BriefField label="Situation — Ground"   value={w.situation_ground} />}
              {w.situation_enemy    && <BriefField label="Situation — Enemy"    value={w.situation_enemy} />}
              {w.situation_friendly && <BriefField label="Situation — Friendly" value={w.situation_friendly} />}
              {w.mission            && <BriefField label="Mission"              value={w.mission} />}
              {w.timings_hh         && <BriefField label="H-Hour"               value={w.timings_hh} />}
              {w.timings_nmb        && <BriefField label="No Move Before"       value={w.timings_nmb} />}
              {w.timings_other      && <BriefField label="Other Timings"        value={w.timings_other} />}
              {w.css                && <BriefField label="CSS / Logistics"      value={w.css} />}
              {(w.o_group_time || w.o_group_loc) && (
                <BriefField label="O Group" value={[w.o_group_time && `Time: ${w.o_group_time}`, w.o_group_loc && `Location: ${w.o_group_loc}`, w.o_group_other].filter(Boolean).join(" · ")} />
              )}
              <p className="text-xs text-muted-foreground font-sans pt-1">Issued {formatDistanceToNow(new Date(w.created_date), { addSuffix: true })}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── SITREPs tab ─────────────────────────────────────────────────────────── */
function MemberSitrepsTab({ group, showMsg, rosterEntry }: any) {
  const [sitreps, setSitreps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string|null>(null);

  useEffect(() => {
    apiFetch<any>(`/milsimSitrep?path=list&group_id=${group.id}`)
      .then((r: any) => setSitreps((r.sitreps ?? []).sort((a: any, b: any) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime())))
      .catch(() => setSitreps([]))
      .finally(() => setLoading(false));
  }, [group.id]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (sitreps.length === 0) return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg">
      <Activity className="w-10 h-10 mx-auto mb-3 opacity-20" />
      <p className="font-display text-sm uppercase tracking-widest text-muted-foreground">No SITREPs filed</p>
      <p className="text-xs text-muted-foreground font-sans mt-1">Situation reports from your commander will appear here</p>
    </div>
  );

  const myCallsign = rosterEntry?.callsign ?? "";

  return (
    <div className="space-y-3 max-w-3xl">
      <p className="text-xs text-muted-foreground font-sans">{sitreps.length} situation report{sitreps.length !== 1 ? "s" : ""}</p>
      {sitreps.map((s: any) => (
        <div key={s.id} className="bg-card border border-border rounded-lg overflow-hidden">
          <button onClick={() => setExpanded(expanded === s.id ? null : s.id)}
            className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-secondary/20 transition-colors text-left">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
                s.mission_status === "on_track" ? "text-green-400 border-green-500/30 bg-green-500/10" :
                s.mission_status === "compromised" ? "text-red-400 border-red-500/30 bg-red-500/10" :
                s.mission_status === "delayed" ? "text-yellow-400 border-yellow-500/30 bg-yellow-500/10" :
                "text-muted-foreground border-border"
              }`}>{(s.mission_status ?? "unknown").replace("_", " ")}</span>
              <span className="font-display font-bold text-sm">{s.op_name ?? "SITREP"}</span>
              <span className="text-xs text-muted-foreground">by {s.callsign}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0 text-muted-foreground">
              <span className="text-xs font-sans">{formatDistanceToNow(new Date(s.created_date), { addSuffix: true })}</span>
              {expanded === s.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </button>
          {expanded === s.id && (
            <div className="border-t border-border p-5 bg-secondary/10 space-y-4">
              {s.position        && <BriefField label="Position"            value={s.position} />}
              {s.activity        && <BriefField label="Current Activity"    value={s.activity} />}
              {s.enemy_contact   && <BriefField label="Enemy Contact"       value={s.enemy_contact} />}
              {s.enemy_notes     && <BriefField label="Enemy Notes"         value={s.enemy_notes} />}
              {s.mission_notes   && <BriefField label="Mission Status"      value={s.mission_notes} />}
              {s.next_action     && <BriefField label="Next Action"         value={s.next_action} />}
              {s.additional_info && <BriefField label="Additional Info"     value={s.additional_info} />}
              {s.friendly_casualties > 0 && <BriefField label="Friendly Casualties" value={`${s.friendly_casualties}${s.casualty_notes ? ` — ${s.casualty_notes}` : ""}`} />}
              <p className="text-xs text-muted-foreground font-sans pt-1">Filed by {s.reported_by} · {formatDistanceToNow(new Date(s.created_date), { addSuffix: true })}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── AARs tab (read + submit witness confirmation) ───────────────────────── */
function MemberAARsTab({ group, showMsg, rosterEntry }: any) {
  const [aars, setAars] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string|null>(null);

  const load = useCallback(() => {
    apiFetch<any>(`/milsimAars?path=list&group_id=${group.id}`)
      .then((r: any) => setAars((r.aars ?? []).sort((a: any, b: any) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime())))
      .catch(() => setAars([]))
      .finally(() => setLoading(false));
  }, [group.id]);

  useEffect(() => { load(); }, [load]);


  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (aars.length === 0) return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg">
      <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-20" />
      <p className="font-display text-sm uppercase tracking-widest text-muted-foreground">No AARs filed yet</p>
      <p className="text-xs text-muted-foreground font-sans mt-1">Post-operation reports filed by your commander appear here</p>
    </div>
  );

  const myCallsign = rosterEntry?.callsign ?? "";

  return (
    <div className="space-y-3 max-w-3xl">
      <p className="text-xs text-muted-foreground font-sans">{aars.length} after-action report{aars.length !== 1 ? "s" : ""}</p>
      {aars.map((a: any) => {
        const participants: string[] = Array.isArray(a.participants) ? a.participants : [];
        const wasParticipant = myCallsign && participants.some((p: string) => (p ?? "").toLowerCase() === (myCallsign ?? "").toLowerCase());
        return (
          <div key={a.id} className="bg-card border border-border rounded-lg overflow-hidden">
            <button onClick={() => setExpanded(expanded === a.id ? null : a.id)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-secondary/20 transition-colors text-left">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${CL[a.classification ?? "unclassified"] ?? ""}`}>
                  {(a.classification ?? "unclassified").replace("-"," ")}
                </span>
                <span className="font-display font-bold text-sm">{a.op_name ?? a.title}</span>
                {wasParticipant && (
                  <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border text-cyan-400 border-cyan-500/30 bg-cyan-500/10">
                    ✓ You Participated
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0 text-muted-foreground">
                {a.op_date && <span className="text-xs">{format(new Date(a.op_date + "T00:00:00"), "dd MMM yyyy")}</span>}
                {expanded === a.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </button>
            {expanded === a.id && (
              <div className="border-t border-border p-5 bg-secondary/10 space-y-3">
                {a.content   && <BriefField label="Summary"           value={a.content} />}
                {a.objectives_hit   && <BriefField label="Objectives Hit"   value={a.objectives_hit} />}
                {a.objectives_missed && <BriefField label="Objectives Missed" value={a.objectives_missed} />}
                {a.casualties       && <BriefField label="Casualties"        value={a.casualties} />}
                {a.commendations    && <BriefField label="Commendations"     value={a.commendations} />}
                {a.lessons_learned  && <BriefField label="Lessons Learned"   value={a.lessons_learned} />}
                {participants.length > 0 && (
                  <div>
                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Participants</p>
                    <div className="flex flex-wrap gap-1.5">
                      {participants.map((p: string, i: number) => (
                        <span key={i} className={`text-[10px] px-2 py-0.5 rounded border font-display ${(p ?? "").toLowerCase() === (myCallsign ?? "").toLowerCase() ? 'text-cyan-400 border-cyan-500/30 bg-cyan-500/10' : 'text-muted-foreground border-border bg-secondary/40'}`}>{p}</span>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground font-sans pt-1">Filed by {a.author_username} · {formatDistanceToNow(new Date(a.created_date), { addSuffix: true })}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Peer Review tab ─────────────────────────────────────────────────────── */
function MemberPeerReviewTab({ group, showMsg, user }: any) {
  const [roster, setRoster] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any|null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<string[]>([]);
  const [form, setForm] = useState({ activity: 7, attitude: 7, experience: 5, discipline: 7, overall_vote: "commend", notes: "" });

  useEffect(() => {
    apiFetch<any>(`/milsimGroups?path=${group.id}/full`)
      .then((g: any) => {
        // Filter out self
        const others = (g.roster ?? []).filter((r: any) => r.userId !== (user as any)?.id && r.user_id !== (user as any)?.id);
        setRoster(others);
      })
      .catch(() => setRoster([]))
      .finally(() => setLoading(false));
  }, [group.id, user]);

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await apiFetch(`/reputation?path=${selected.userId}/peer`, {
        method: "POST",
        body: JSON.stringify({ ...form, group_id: group.id, group_name: group.name }),
      });
      setSubmitted(prev => [...prev, selected.id]);
      setSelected(null);
      setForm({ activity: 7, attitude: 7, experience: 5, discipline: 7, overall_vote: "commend", notes: "" });
      showMsg(true, "Peer review submitted.");
    } catch (e: any) {
      showMsg(false, e.message);
    } finally { setSubmitting(false); }
  };


  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="bg-secondary/30 border border-border rounded-lg p-4 text-sm font-sans text-muted-foreground leading-relaxed">
        <p className="font-display font-bold uppercase tracking-widest text-xs text-foreground mb-1">How Peer Reviews Work</p>
        <p>Rate your fellow operators after shared operations. Scores are aggregated across <strong className="text-foreground">all units they've served in</strong> and displayed on their public service file. This helps commanders across the registry gauge operator quality. One review per member — honest assessments only.</p>
      </div>

      {roster.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg text-muted-foreground">
          <p className="font-display text-sm uppercase tracking-widest">No other roster members to review</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Roster list */}
          <div className="space-y-2">
            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-3">Select Operator to Review</p>
            {roster.map((m: any) => {
              const done = submitted.includes(m.id);
              const isSelected = selected?.id === m.id;
              return (
                <button key={m.id} onClick={() => !done && setSelected(m)} disabled={done}
                  className={`w-full text-left flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${
                    done ? "opacity-40 cursor-not-allowed border-border" :
                    isSelected ? "border-primary/50 bg-primary/5" :
                    "border-border hover:border-primary/30 bg-card"
                  }`}>
                  <div>
                    <p className="font-display font-bold uppercase tracking-wider text-sm">{m.callsign}</p>
                    {m.rankName && <p className="text-xs text-muted-foreground">{m.rankName}</p>}
                  </div>
                  {done && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                </button>
              );
            })}
          </div>

          {/* Review form */}
          <div>
            {!selected ? (
              <div className="flex flex-col items-center justify-center h-64 border border-dashed border-border rounded-lg text-muted-foreground text-sm text-center px-4">
                <UserCheck className="w-8 h-8 mb-3 opacity-20" />
                <p className="font-display text-xs uppercase tracking-widest">Select an operator to leave a review</p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg p-5 space-y-4">
                <p className="font-display font-black uppercase tracking-widest text-base">{selected.callsign}</p>
                {(["activity","attitude","experience","discipline"] as const).map(field => (
                  <div key={field}>
                    <div className="flex justify-between mb-1">
                      <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground capitalize">{field}</label>
                      <span className="text-xs font-bold text-primary">{(form as any)[field]}/10</span>
                    </div>
                    <input type="range" min={1} max={10} value={(form as any)[field]}
                      onChange={e => setForm(f => ({...f, [field]: parseInt(e.target.value)}))}
                      className="w-full accent-primary" />
                  </div>
                ))}
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-2">Overall Assessment</label>
                  <div className="flex gap-2">
                    {[{v:"commend",label:"Commend",cls:"border-green-500/40 text-green-400"},{v:"neutral",label:"Neutral",cls:"border-border text-muted-foreground"},{v:"caution",label:"Caution",cls:"border-amber-500/40 text-amber-400"}].map(o => (
                      <button key={o.v} onClick={() => setForm(f => ({...f, overall_vote: o.v}))}
                        className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest border rounded transition-all ${form.overall_vote === o.v ? o.cls + ' bg-secondary/50' : 'border-border text-muted-foreground'}`}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">Notes (optional)</label>
                  <textarea rows={3} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                    className="w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans resize-none focus:outline-none focus:ring-1 focus:ring-primary/50"
                    placeholder="Brief note about your experience serving with this operator..." />
                </div>
                <div className="flex gap-2">
                  <button onClick={submit} disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-widest text-xs py-2.5 rounded transition-all disabled:opacity-50">
                    {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Submit Review
                  </button>
                  <button onClick={() => setSelected(null)}
                    className="px-4 border border-border rounded text-xs font-display uppercase hover:bg-secondary transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── LOA Request tab ─────────────────────────────────────────────────────── */
function MemberLOATab({ group, showMsg, user, rosterEntry }: any) {
  const [myLoas, setMyLoas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const emptyForm = { reason_category: "Personal", reason_detail: "", start_date: "", end_date: "" };
  const [form, setForm] = useState<any>(emptyForm);

  const CATEGORIES = ["Personal", "Medical", "Work / School", "Travel", "Military Service", "Family", "Other"];

  const load = useCallback(() => {
    if (!rosterEntry) { setLoading(false); return; }
    apiFetch<any>(`/loa?path=list&group_id=${group.id}`)
      .then((r: any) => {
        const all = r.loas ?? [];
        const mine = all.filter((l: any) => l.roster_id === rosterEntry.id || l.user_id === (user as any)?.id);
        setMyLoas(mine.sort((a: any, b: any) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime()));
      })
      .catch(() => setMyLoas([]))
      .finally(() => setLoading(false));
  }, [group.id, rosterEntry, user]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.start_date || !form.end_date) { showMsg(false, "Start and end date required"); return; }
    if (!rosterEntry) { showMsg(false, "You must be on the roster to request LOA"); return; }
    setSubmitting(true);
    try {
      await apiFetch(`/loa?path=create`, {
        method: "POST",
        body: JSON.stringify({
          group_id: group.id,
          roster_id: rosterEntry.id,
          user_id: (user as any)?.id,
          callsign: rosterEntry.callsign,
          ...form,
        }),
      });
      showMsg(true, "LOA request submitted — awaiting commander approval");
      setShowForm(false);
      setForm(emptyForm);
      load();
    } catch (e: any) { showMsg(false, e.message); }
    finally { setSubmitting(false); }
  };

  const LOA_STATUS_CLR: Record<string, string> = {
    Pending:  "text-amber-400 border-amber-500/30 bg-amber-500/10",
    Approved: "text-green-400 border-green-500/30 bg-green-500/10",
    Active:   "text-primary border-primary/30 bg-primary/10",
    Denied:   "text-destructive border-destructive/30 bg-destructive/10",
    Expired:  "text-muted-foreground border-border",
  };

  if (!rosterEntry) return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg">
      <AlertTriangle className="w-8 h-8 mx-auto mb-3 opacity-30" />
      <p className="text-sm font-display uppercase tracking-widest text-muted-foreground">Roster entry not found — contact your commander</p>
    </div>
  );


  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground font-sans">Submit a Leave of Absence request. Your commander will approve or deny it.</p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-widest text-xs px-4 py-2 rounded transition-all">
            <PlusCircle className="w-3.5 h-3.5" /> Request LOA
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-card border border-primary/30 rounded-lg p-5 space-y-4">
          <h3 className="font-display font-bold uppercase tracking-widest text-sm">New LOA Request</h3>
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">Category</label>
            <select value={form.reason_category} onChange={e => setForm((f: any) => ({...f, reason_category: e.target.value}))}
              className="w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">Brief Reason</label>
            <textarea rows={3} value={form.reason_detail} onChange={e => setForm((f: any) => ({...f, reason_detail: e.target.value}))}
              className="w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans resize-none"
              placeholder="Brief explanation (not shared publicly)..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">Start Date</label>
              <input type="date" value={form.start_date} onChange={e => setForm((f: any) => ({...f, start_date: e.target.value}))}
                className="w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans" />
            </div>
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">End Date</label>
              <input type="date" value={form.end_date} onChange={e => setForm((f: any) => ({...f, end_date: e.target.value}))}
                className="w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans" />
            </div>
          </div>
          {form.start_date && form.end_date && (
            <p className="text-xs text-muted-foreground font-sans">
              Duration: {differenceInCalendarDays(new Date(form.end_date), new Date(form.start_date))} day(s)
            </p>
          )}
          <div className="flex gap-2">
            <button onClick={submit} disabled={submitting}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded disabled:opacity-50">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Submit Request
            </button>
            <button onClick={() => { setShowForm(false); setForm(emptyForm); }}
              className="px-4 border border-border rounded text-xs font-display uppercase hover:bg-secondary">
              Cancel
            </button>
          </div>
        </div>
      )}

      {myLoas.length === 0 && !showForm ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg text-muted-foreground">
          <Clock className="w-8 h-8 mx-auto mb-3 opacity-20" />
          <p className="text-sm font-display uppercase tracking-widest">No LOA history</p>
        </div>
      ) : (
        <div className="space-y-2">
          {myLoas.map((l: any) => (
            <div key={l.id} className="bg-card border border-border rounded-lg p-4 flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${LOA_STATUS_CLR[l.status] ?? ""}`}>{l.status}</span>
                  <span className="font-display font-bold text-sm">{l.reason_category}</span>
                </div>
                <p className="text-xs text-muted-foreground font-sans">
                  {l.start_date} → {l.end_date}
                  {l.reason_detail && <span> · {l.reason_detail}</span>}
                </p>
                {l.status === "Denied" && l.review_note && (
                  <p className="text-xs text-destructive mt-1 font-sans">Reason: {l.review_note}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ─── Unit Review tab ────────────────────────────────────────────────────── */
const REVIEWS_URL = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions/groupReviews";

function StarRating({ value, onChange, readonly = false }: { value: number; onChange?: (v: number) => void; readonly?: boolean }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button" disabled={readonly}
          onClick={() => !readonly && onChange?.(n)}
          className={`transition-colors ${readonly ? "cursor-default" : "cursor-pointer hover:scale-110"}`}>
          <Star className={`w-5 h-5 ${n <= value ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`} />
        </button>
      ))}
    </div>
  );
}

function SubScoreRow({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  const colours = ["","bg-red-500","bg-orange-500","bg-yellow-500","bg-blue-500","bg-green-500"];
  const labels  = ["","Poor","Below Avg","Average","Good","Excellent"];
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground w-28 shrink-0">{label}</span>
      <div className="flex gap-1">
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" onClick={() => onChange(n)}
            className={`w-7 h-7 rounded text-xs font-bold border transition-all ${value === n ? `${colours[n]} text-white border-transparent` : "bg-secondary border-border text-muted-foreground hover:border-primary/40"}`}>
            {n}
          </button>
        ))}
      </div>
      <span className="text-[9px] text-muted-foreground">{labels[value] ?? ""}</span>
    </div>
  );
}

function MemberUnitReviewTab({ group, showMsg, user }: any) {
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting]     = useState<any | null>(null);
  const [editing, setEditing]       = useState(false);
  const [form, setForm] = useState({
    rating: 4,
    organisation: 3,
    communication: 3,
    gameplay: 3,
    community: 3,
    headline: "",
    body: "",
    served_months: "" as number | "",
    recommend: true,
  });

  // Load the user's existing review for this group
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("tag_token") ?? "";
      const res = await fetch(`${REVIEWS_URL}?path=%2Fmine&group_id=${group.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data?.id) {
        setExisting(data);
        setForm({
          rating:        data.rating ?? 4,
          organisation:  data.organisation ?? 3,
          communication: data.communication ?? 3,
          gameplay:      data.gameplay ?? 3,
          community:     data.community ?? 3,
          headline:      data.headline ?? "",
          body:          data.body ?? "",
          served_months: data.served_months ?? "",
          recommend:     data.recommend ?? true,
        });
      }
    } catch {}
    setLoading(false);
  }, [group.id]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.headline.trim()) { showMsg(false, "Please add a headline for your review"); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("tag_token") ?? "";
      const isEdit = !!existing;
      const url = isEdit
        ? `${REVIEWS_URL}?path=%2Fupdate&id=${existing.id}`
        : `${REVIEWS_URL}?path=%2Fcreate`;
      const res = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, group_id: group.id, group_name: group.name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to submit review");
      showMsg(true, isEdit ? "Review updated!" : "Review published!");
      setEditing(false);
      await load();
    } catch (e: any) {
      showMsg(false, e.message ?? "Failed to submit review");
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!existing || !confirm("Delete your review? This cannot be undone.")) return;
    try {
      const token = localStorage.getItem("tag_token") ?? "";
      await fetch(`${REVIEWS_URL}?path=%2Fdelete&id=${existing.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      showMsg(true, "Review deleted");
      setExisting(null);
      setEditing(false);
      setForm({ rating:4, organisation:3, communication:3, gameplay:3, community:3, headline:"", body:"", served_months:"", recommend:true });
    } catch { showMsg(false, "Failed to delete review"); }
  };

  const RATING_LABEL: Record<number,string> = { 1:"Terrible", 2:"Poor", 3:"Okay", 4:"Good", 5:"Outstanding" };
  const RATING_COLOR: Record<number,string> = { 1:"text-red-400", 2:"text-orange-400", 3:"text-yellow-400", 4:"text-blue-400", 5:"text-green-400" };


  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  // ── Existing review display ───────────────────────────────────────────────
  if (existing && !editing) return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-secondary/40 border border-border rounded-lg p-4 text-xs font-sans text-muted-foreground">
        <p className="font-display font-bold uppercase tracking-widest text-xs text-foreground mb-1">Your Review is Live</p>
        <p>This review is visible on <strong className="text-foreground">{group.name}</strong>'s public registry page. You can edit or delete it at any time.</p>
      </div>

      <div className="border border-border bg-card rounded-xl p-5 space-y-4">
        {/* Stars + headline */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <StarRating value={existing.rating} readonly />
            <p className={`text-sm font-display font-bold mt-1 ${RATING_COLOR[existing.rating] ?? "text-foreground"}`}>{RATING_LABEL[existing.rating]}</p>
          </div>
          {existing.recommend !== undefined && (
            <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-1 rounded border flex items-center gap-1 ${existing.recommend ? "border-green-500/40 text-green-400 bg-green-500/5" : "border-red-500/40 text-red-400 bg-red-500/5"}`}>
              {existing.recommend ? <ThumbsUp className="w-3 h-3" /> : <ThumbsDown className="w-3 h-3" />}
              {existing.recommend ? "Recommended" : "Not Recommended"}
            </span>
          )}
        </div>
        <div>
          <p className="font-display font-bold text-sm text-foreground">{existing.headline}</p>
          {existing.body && <p className="text-xs font-sans text-muted-foreground mt-1.5 leading-relaxed">{existing.body}</p>}
        </div>
        {/* Sub-scores */}
        {(existing.organisation || existing.communication || existing.gameplay || existing.community) && (
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 border-t border-border pt-3">
            {[["Organisation", existing.organisation],["Communication", existing.communication],["Gameplay",existing.gameplay],["Community",existing.community]].map(([l,v]) =>
              v ? (
                <div key={l} className="flex items-center justify-between">
                  <span className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">{l}</span>
                  <span className="text-xs font-display font-bold text-foreground">{v}/5</span>
                </div>
              ) : null
            )}
          </div>
        )}
        {existing.served_months && (
          <p className="text-[10px] font-sans text-muted-foreground border-t border-border pt-2">Served approx. {existing.served_months} month{existing.served_months !== 1 ? "s" : ""}</p>
        )}
      </div>

      <div className="flex gap-3">
        <button onClick={() => setEditing(true)} className="flex items-center gap-2 px-4 py-1.5 bg-primary/20 border border-primary/40 text-primary rounded font-display text-xs uppercase tracking-widest hover:bg-primary/30 transition-colors">
          Edit Review
        </button>
        <button onClick={handleDelete} className="flex items-center gap-2 px-4 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 rounded font-display text-xs uppercase tracking-widest hover:bg-red-500/20 transition-colors">
          Delete
        </button>
      </div>
    </div>
  );

  // ── Write / Edit form ─────────────────────────────────────────────────────
  return (
    <div className="space-y-6 max-w-2xl">
      {/* Info banner */}
      <div className="bg-secondary/40 border border-border rounded-lg p-4 text-xs font-sans text-muted-foreground leading-relaxed">
        <p className="font-display font-bold uppercase tracking-widest text-xs text-foreground mb-1">
          {editing ? "Edit Your Review" : "Write a Unit Review"}
        </p>
        <p>
          Your review of <strong className="text-foreground">{group.name}</strong> will be publicly visible on their registry listing page. Only current or former roster members can submit reviews. Be honest — this helps other operators make informed decisions.
        </p>
      </div>

      <div className="border border-border bg-card rounded-xl p-5 space-y-5">
        {/* Overall rating */}
        <div>
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Overall Rating *</p>
          <StarRating value={form.rating} onChange={v => setForm(f => ({ ...f, rating: v }))} />
          <p className={`text-xs font-display font-bold mt-1 ${RATING_COLOR[form.rating] ?? "text-foreground"}`}>{RATING_LABEL[form.rating]}</p>
        </div>

        {/* Headline */}
        <div>
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Headline *</p>
          <input
            value={form.headline}
            onChange={e => setForm(f => ({ ...f, headline: e.target.value }))}
            placeholder='e.g. "Outstanding unit, highly professional command structure"'
            maxLength={120}
            className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
          />
        </div>

        {/* Body */}
        <div>
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Full Review <span className="font-normal normal-case">(optional)</span></p>
          <textarea
            value={form.body}
            onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
            placeholder="Share your experience — ops quality, leadership, community atmosphere, training standards..."
            rows={4}
            className="w-full bg-secondary border border-border rounded px-3 py-2 text-xs font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 resize-none"
          />
        </div>

        {/* Sub-scores */}
        <div>
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-3">Category Scores <span className="font-normal normal-case">(optional)</span></p>
          <div className="space-y-2.5">
            <SubScoreRow label="Organisation" value={form.organisation} onChange={v => setForm(f => ({ ...f, organisation: v }))} />
            <SubScoreRow label="Communication" value={form.communication} onChange={v => setForm(f => ({ ...f, communication: v }))} />
            <SubScoreRow label="Gameplay" value={form.gameplay} onChange={v => setForm(f => ({ ...f, gameplay: v }))} />
            <SubScoreRow label="Community" value={form.community} onChange={v => setForm(f => ({ ...f, community: v }))} />
          </div>
        </div>

        {/* Time served + recommend */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-border pt-4">
          <div>
            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Time Served (months)</p>
            <input
              type="number" min="1" max="120"
              value={form.served_months}
              onChange={e => setForm(f => ({ ...f, served_months: e.target.value === "" ? "" : Number(e.target.value) }))}
              placeholder="e.g. 6"
              className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
            />
          </div>
          <div>
            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Would you recommend?</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setForm(f => ({ ...f, recommend: true }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-display font-bold uppercase tracking-widest transition-all ${form.recommend ? "bg-green-500/20 border-green-500/50 text-green-400" : "bg-secondary border-border text-muted-foreground hover:border-green-500/30"}`}>
                <ThumbsUp className="w-3.5 h-3.5" /> Yes
              </button>
              <button type="button" onClick={() => setForm(f => ({ ...f, recommend: false }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-display font-bold uppercase tracking-widest transition-all ${!form.recommend ? "bg-red-500/20 border-red-500/50 text-red-400" : "bg-secondary border-border text-muted-foreground hover:border-red-500/30"}`}>
                <ThumbsDown className="w-3.5 h-3.5" /> No
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button onClick={handleSubmit} disabled={submitting || !form.headline.trim()}
            className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded font-display text-xs uppercase tracking-widest hover:bg-primary/90 transition-all disabled:opacity-50">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {editing ? "Save Changes" : "Publish Review"}
          </button>
          {editing && (
            <button onClick={() => setEditing(false)} className="px-4 py-2 border border-border rounded font-display text-xs uppercase tracking-widest text-muted-foreground hover:border-primary/40 transition-colors">
              Cancel
            </button>
          )}
        </div>
      </div>

      <p className="text-[10px] text-muted-foreground font-sans">
        Reviews are moderated. Abusive, misleading, or spam reviews will be removed. One review per unit.
      </p>
    </div>
  );
}

/* ─── Qualifications Tab ──────────────────────────────────────────────────── */
function MemberQualificationsTab({ rosterEntry, groupDetail }: any) {
  const quals: any[] = rosterEntry?.qualifications ?? [];
  const qualDefs: any[] = groupDetail?.qualDefs ?? groupDetail?.questions ?? []; // fallback

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-secondary/30 border border-border rounded-lg p-4 text-sm font-sans text-muted-foreground">
        <p>These are your <strong className="text-foreground">issued qualifications</strong> — awarded by your unit commander. Qualifications are read-only; contact your commander if any are missing or incorrect.</p>
      </div>

      {quals.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-20" />
          <p className="font-display text-sm uppercase tracking-widest text-muted-foreground">No Qualifications Issued</p>
          <p className="text-xs text-muted-foreground font-sans mt-1">Your commander hasn't issued any qualifications yet</p>
        </div>
      ) : (
        <div>
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-3">{quals.length} qualification{quals.length !== 1 ? "s" : ""} on record</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {quals.map((q: any) => (
              <div key={q.id} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4">
                {q.badge_url ? (
                  <img
                    src={q.badge_url}
                    alt={q.name}
                    className="w-14 h-14 rounded object-contain border border-border bg-secondary/40 shrink-0"
                    onError={(e: any) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-14 h-14 rounded border border-border bg-secondary/40 flex items-center justify-center shrink-0">
                    <GraduationCap className="w-6 h-6 text-muted-foreground/40" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-display font-bold text-sm text-foreground truncate">{q.name}</p>
                  {q.notes && <p className="text-xs text-muted-foreground font-sans mt-0.5 leading-relaxed line-clamp-2">{q.notes}</p>}
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="text-[9px] font-display uppercase tracking-widest text-primary border border-primary/30 bg-primary/10 px-1.5 py-0.5 rounded">Qualified</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Service File tab ────────────────────────────────────────────────────── */
function MemberServiceFileTab({ user, rosterEntry, groupDetail }: any) {
  const [rep, setRep] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    apiFetch<any>(`/reputation?path=${user.id}`)
      .then(setRep)
      .catch(() => setRep(null))
      .finally(() => setLoading(false));
  }, [user?.id]);


  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const GRADE_COLORS: Record<string, string> = {
    ELITE: "#fbbf24", TRUSTED: "#4ade80", STANDARD: "#60a5fa",
    CAUTION: "#fb923c", "HIGH RISK": "#f87171", BLACKLISTED: "#ef4444", UNRATED: "#6b7280",
  };

  const score = rep?.score;

  // Resolve rank/role names from groupDetail
  const ranks: any[] = groupDetail?.ranks ?? [];
  const roles: any[] = groupDetail?.roles ?? [];
  const rank = rosterEntry?.rankId ? ranks.find((r: any) => r.id === rosterEntry.rankId) : null;
  const role = rosterEntry?.roleId ? roles.find((r: any) => r.id === rosterEntry.roleId) : null;
  const rankName = rank?.name ?? rosterEntry?.rank_name ?? null;
  const rankAbbr = rank?.abbreviation ?? null;
  const roleName = role?.name ?? rosterEntry?.roleName ?? null;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-secondary/30 border border-border rounded-lg p-4 text-sm font-sans text-muted-foreground">
        <p>This is your <strong className="text-foreground">public operator service file</strong> — what commanders see when they look you up. Scores are built from commander and peer reviews across all units you've served in.</p>
      </div>

      {/* Rank & Role read-only card */}
      {rosterEntry && (rankName || roleName || rosterEntry.callsign || rosterEntry.specialisations) && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-primary" />
            <h3 className="font-display font-bold uppercase tracking-widest text-xs text-foreground">Rank &amp; Assignment</h3>
            <span className="ml-auto text-[9px] font-display uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5 rounded">Read Only</span>
          </div>
          <div className="px-5 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {rosterEntry.callsign && (
                <div className="bg-secondary/40 border border-border rounded p-2.5">
                  <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">Callsign</p>
                  <p className="text-sm font-display font-black text-primary">{rosterEntry.callsign}</p>
                </div>
              )}
              {rankName && (
                <div className="bg-secondary/40 border border-border rounded p-2.5">
                  <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">Rank</p>
                  <p className="text-sm font-display font-bold text-foreground">{rankAbbr ? `${rankAbbr} — ${rankName}` : rankName}</p>
                </div>
              )}
              {roleName && (
                <div className="bg-secondary/40 border border-border rounded p-2.5">
                  <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">Role / Position</p>
                  <p className="text-sm font-display font-bold text-foreground">{roleName}</p>
                </div>
              )}
              {rosterEntry.status && (
                <div className="bg-secondary/40 border border-border rounded p-2.5">
                  <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">Status</p>
                  <p className={`text-sm font-display font-bold capitalize ${
                    rosterEntry.status === "active" ? "text-green-400" :
                    rosterEntry.status === "reserve" ? "text-amber-400" :
                    rosterEntry.status === "loa" ? "text-blue-400" :
                    "text-muted-foreground"
                  }`}>{rosterEntry.status}</p>
                </div>
              )}
            </div>
            {rosterEntry.specialisations && rosterEntry.specialisations.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border/40">
                <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-2">Specialisations</p>
                <div className="flex flex-wrap gap-1.5">
                  {(Array.isArray(rosterEntry.specialisations) ? rosterEntry.specialisations : [rosterEntry.specialisations]).map((s: string, i: number) => (
                    <span key={i} className="text-[10px] font-display uppercase tracking-wider bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 rounded">{s}</span>
                  ))}
                </div>
              </div>
            )}
            {rosterEntry.notes && (
              <div className="mt-3 pt-3 border-t border-border/40">
                <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-1">Commander Notes on File</p>
                <p className="text-xs font-sans text-muted-foreground italic">{rosterEntry.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {!score || score.grade === "UNRATED" ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-20" />
          <p className="font-display text-sm uppercase tracking-widest text-muted-foreground">No reviews yet</p>
          <p className="text-xs text-muted-foreground font-sans mt-1">Your service file will populate as commanders and peers submit reviews after operations</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Overall */}
          <div className="bg-card border border-border rounded-lg p-6 flex items-center gap-6">
            <div className="text-center">
              <p className="text-6xl font-black" style={{ color: GRADE_COLORS[score.grade] ?? "#6b7280" }}>{score.overall}</p>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GRADE_COLORS[score.grade] ?? "#6b7280" }}>{score.grade}</p>
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Activity",   value: score.activity },
                  { label: "Attitude",   value: score.attitude },
                  { label: "Experience", value: score.experience },
                  { label: "Discipline", value: score.discipline },
                ].map(s => (
                  <div key={s.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground font-display uppercase tracking-wider">{s.label}</span>
                      <span className="font-bold text-foreground">{s.value?.toFixed(1) ?? "—"}</span>
                    </div>
                    <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${((s.value ?? 0) / 10) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground font-sans mt-3">{rep.review_count} review{rep.review_count !== 1 ? "s" : ""} submitted</p>
            </div>
          </div>
          {/* Reviews list */}
          {Array.isArray(rep.reviews) && rep.reviews.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Individual Reviews</p>
              {rep.reviews.map((r: any, i: number) => (
                <div key={i} className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-display font-bold text-xs uppercase tracking-widest text-foreground">{r.group_name ?? "Anonymous"}</p>
                      {r.notes && <p className="text-xs text-muted-foreground font-sans mt-1 leading-relaxed">{r.notes}</p>}
                    </div>
                    <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border shrink-0 ${
                      r.overall_vote === "commend" ? "text-green-400 border-green-500/30" :
                      r.overall_vote === "caution" ? "text-amber-400 border-amber-500/30" :
                      "text-muted-foreground border-border"
                    }`}>{r.overall_vote}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Ribbon Bar Tab ──────────────────────────────────────────────────────────
   Shows only if the member's unit has CommanderPro.
   Pulls all Ribbon-type awards granted to this member, lets them drag/reorder
   and build their ribbon rack. Saves order back to their roster record.
   ─────────────────────────────────────────────────────────────────────────── */

// Map award names to uniformribbons.com image slugs (for units using US-style ribbons)
const RIBBON_SLUG_MAP: Record<string, string> = {
  "Medal of Honor":                             "medal_of_honor_ribbon",
  "Distinguished Service Cross":                "army_distinguished_service_cross_ribbon",
  "Defense Distinguished Service":              "defense_distinguished_service_ribbon",
  "Distinguished Service":                      "army_distinguished_service_ribbon",
  "Silver Star":                                "silver_star_ribbon",
  "Defense Superior Service":                   "defense_superior_service_ribbon",
  "Legion of Merit":                            "legion_of_merit_ribbon",
  "Distinguished Flying Cross":                 "distinguished_flying_cross_ribbon",
  "Soldier's Medal":                            "soldier_medal_ribbon",
  "Bronze Star":                                "bronze_star_ribbon",
  "Purple Heart":                               "purple_heart_ribbon",
  "Defense Meritorious Service":                "defense_meritorious_service_ribbon",
  "Meritorious Service":                        "meritorious_service_ribbon",
  "Air Medal":                                  "air_medal_ribbon",
  "Joint Service Commendation":                 "joint_service_commendation_ribbon",
  "Army Commendation":                          "army_commendation_ribbon",
  "Joint Service Achievement":                  "joint_service_achievement_ribbon",
  "Army Achievement":                           "army_achievement_ribbon",
  "Good Conduct":                               "army_good_conduct_ribbon",
  "National Defense Service":                   "national_defense_service_ribbon",
  "Armed Forces Expeditionary":                 "armed_forces_expeditionary_ribbon",
  "Global War on Terrorism Expeditionary":      "global_war_on_terrorism_expeditionary_ribbon",
  "Global War on Terrorism Service":            "global_war_terrorism_service_ribbon",
  "Korea Defense Service":                      "korea_defense_service_ribbon",
  "Armed Forces Service":                       "armed_forces_service_ribbon",
  "Humanitarian Service":                       "humanitarian_service_ribbon",
  "Army Service":                               "army_service_ribbon",
  "Overseas Service":                           "army_overseas_service_ribbon",
  "NATO Medal":                                 "nato_medal_meritorious_ribbon",
  "NATO ISAF":                                  "nato_isaf_ribbon",
  "United Nations Medal":                       "united_nations_medal_ribbon",
  "Afghanistan Campaign":                       "afghanistan_campaign_medal_ribbon",
  "Iraq Campaign":                              "iraq_campaign_medal_ribbon",
  "Kosovo Campaign":                            "kosovo_campaign_medal_ribbon",
  "Southwest Asia Service":                     "southwest_asia_service_medal_ribbon",
  "Vietnam Service":                            "vietnam_service_medal_ribbon",
  "Prisoner of War":                            "prisoner_of_war_ribbon",
  "Presidential Unit Citation":                 "army_presidential_unit_citation_ribbon",
  "Valorous Unit Award":                        "army_valorous_unit_award_ribbon",
  "Meritorious Unit Commendation":              "army_meritorious_unit_commendation_ribbon",
  "Superior Unit Award":                        "army_superior_unit_award_ribbon",
};

function ribbonImageUrl(award: any, modifierUrl?: string): string {
  // 0. Modifier override (e.g. w. bar, clasps)
  if (modifierUrl) return modifierUrl;
  // 1. Custom image uploaded by commander
  if (award.award_image_url) return award.award_image_url;
  // 2. Try mapping name to uniformribbons.com asset
  const slug = RIBBON_SLUG_MAP[award.award_name ?? award.name ?? ""];
  if (slug) return `https://www.uniformribbons.com/image/${slug}.png`;
  // 3. Fallback: render CSS ribbon from stripe colours
  return "";
}

function CssRibbon({ award, size = 40 }: { award: any; size?: number }) {
  const c1 = award.ribbon_color_1 ?? "#4a90e2";
  const c2 = award.ribbon_color_2 ?? c1;
  const c3 = award.ribbon_color_3 ?? c2;
  const gradient = `linear-gradient(to right, ${c1} 0%, ${c1} 33%, ${c2} 33%, ${c2} 66%, ${c3} 66%, ${c3} 100%)`;
  return (
    <div
      style={{ width: size * 1.6, height: size * 0.55, background: gradient, borderRadius: 2, flexShrink: 0 }}
      title={award.award_name ?? award.name ?? ""}
    />
  );
}

function RibbonImage({ award, size = 40, modifierUrl, overlayUrl }: { award: any; size?: number; modifierUrl?: string; overlayUrl?: string }) {
  const url = ribbonImageUrl(award, modifierUrl);
  if (!url) return <CssRibbon award={award} size={size} />;
  if (overlayUrl) {
    return (
      <div style={{ position: "relative", width: size * 1.6, height: size * 0.55, flexShrink: 0, borderRadius: 2, overflow: "hidden" }}>
        <img src={url} alt={award.award_name ?? award.name ?? "ribbon"}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "fill" }}
          onError={(e: any) => { e.currentTarget.style.display = "none"; }} />
        <img src={overlayUrl} alt="device"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
          onError={(e: any) => { e.currentTarget.style.display = "none"; }} />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={award.award_name ?? award.name ?? "ribbon"}
      style={{ width: size * 1.6, height: size * 0.55, objectFit: "fill", borderRadius: 2, flexShrink: 0 }}
      onError={(e: any) => { e.currentTarget.style.display = "none"; }}
      title={award.award_name ?? award.name ?? ""}
    />
  );
}

function MemberRibbonBarTab({ group, user, rosterEntry, isPro }: any) {
  const [allRibbons, setAllRibbons]     = useState<any[]>([]); // all awarded ribbons
  const [barIds, setBarIds]             = useState<string[]>([]); // IDs in current bar
  const [barMods, setBarMods]           = useState<Record<string, Record<string, string>>>({}); // modifier selections per ribbon id
  const [dragging, setDragging]         = useState<string | null>(null);
  const [dragOver, setDragOver]         = useState<string | null>(null);
  const [saving, setSaving]             = useState(false);
  const [loading, setLoading]           = useState(true);
  const [hovered, setHovered]           = useState<string | null>(null);
  const [previewMods, setPreviewMods]   = useState<Record<string, Record<string, string>>>({});
  const [localRoster, setLocalRoster]   = useState<any | null>(null);
  const [ribbonSearch, setRibbonSearch] = useState("");
  const [ribbonCountry, setRibbonCountry] = useState("all");
  const [ribbonPage, setRibbonPage]     = useState(1);
  const RIBBONS_PER_PAGE = 30;

  useEffect(() => {
    if (!group?.id || !user?.id) return;
    const load = async () => {
      setLoading(true);
      try {
        // Pro check handled by parent
        if (!isPro) { setLoading(false); return; }

        // Fetch all awards granted to this user in this group
        const [awardsData, rosterData] = await Promise.all([
          apiFetch(`/milsimAwards?path=member_awards&group_id=${group.id}&user_id=${user.id}`),
          apiFetch(`/milsimGroups?path=roster_member&group_id=${group.id}&user_id=${user.id}`),
        ]) as any[];

        // Show all awarded items — medals, orders, decorations all belong in the rack
        const ribbons = awardsData.awards ?? [];
        setAllRibbons(ribbons);

        // Load saved bar order from roster entry
        const member = rosterData.roster_member ?? null;
        setLocalRoster(member);
        const savedOrder: string[] = member?.ribbon_bar_order ?? [];
        const savedMods: Record<string, Record<string, string>> = member?.ribbon_bar_mods ?? {};
        // Keep only IDs that still exist in granted ribbons
        const validIds = savedOrder.filter((id: string) => ribbons.some((r: any) => r.id === id));
        setBarIds(validIds);
        setBarMods(savedMods);
      } catch {}
      setLoading(false);
    };
    load();
  }, [group.id, user?.id]);

  const toggleInBar = (id: string) => {
    setBarIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Drag reorder within bar
  const handleDragStart = (id: string) => setDragging(id);
  const handleDragEnd   = () => { setDragging(null); setDragOver(null); };
  const handleDragOver  = (e: any, id: string) => { e.preventDefault(); setDragOver(id); };
  const handleDrop      = (targetId: string) => {
    if (!dragging || dragging === targetId) { setDragging(null); setDragOver(null); return; }
    setBarIds(prev => {
      const arr = [...prev];
      const fromIdx = arr.indexOf(dragging);
      const toIdx   = arr.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, dragging);
      return arr;
    });
    setDragging(null);
    setDragOver(null);
  };

  const saveBar = async () => {
    const rosterId = localRoster?.id ?? rosterEntry?.id;
    if (!rosterId) { console.warn('[RibbonBar] saveBar: no roster id available'); return; }
    setSaving(true);
    try {
      await apiFetch(`/milsimGroups?path=update_roster_member`, {
        method: "POST",
        body: JSON.stringify({ roster_id: rosterId, ribbon_bar_order: barIds, ribbon_bar_mods: barMods }),
      });
    } catch (e) { console.error('[RibbonBar] saveBar error:', e); }
    setSaving(false);
  };

  // Derive unique countries from awarded ribbons for filter dropdown
  const ribbonCountries = useMemo(() => {
    const seen = new Set<string>();
    allRibbons.forEach((r: any) => {
      const country = r.source_country ?? r.award_description?.match(/^([A-Z]{2,3})\s/)?.[1] ?? null;
      if (country) seen.add(country);
    });
    return Array.from(seen).sort();
  }, [allRibbons]);

  const filteredRibbons = useMemo(() => {
    return allRibbons.filter((r: any) => {
      const name = (r.award_name ?? r.name ?? "").toLowerCase();
      const matchSearch = ribbonSearch === "" || name.includes(ribbonSearch.toLowerCase());
      const country = r.source_country ?? null;
      const matchCountry = ribbonCountry === "all" || country === ribbonCountry;
      return matchSearch && matchCountry;
    });
  }, [allRibbons, ribbonSearch, ribbonCountry]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (!isPro) return (
    <div className="flex flex-col items-center justify-center py-16 space-y-4 text-center">
      <Crown className="w-12 h-12 text-yellow-400/40" />
      <p className="font-display font-black text-sm uppercase tracking-widest text-muted-foreground">Commander Pro Required</p>
      <p className="text-xs text-muted-foreground font-sans max-w-sm">
        The Ribbon Bar builder is a Commander Pro feature. Your unit commander can upgrade at <a href="/commander-pro" className="underline text-primary">commander-pro</a>.
      </p>
    </div>
  );

  // Compute active modifier image URL for a given ribbon
  // Resolve modifier URL + overlay — checks previewMods (and barMods when in rack)
  const getModifierResult = (ribbon: any, usePreview = false): { url?: string; overlayUrl?: string } => {
    const baseUrl = ribbonImageUrl(ribbon);
    const modifiers = getRibbonModifiers(baseUrl);
    if (!modifiers.length) return {};
    const mods = usePreview ? (previewMods[ribbon.id] ?? barMods[ribbon.id] ?? {}) : (barMods[ribbon.id] ?? previewMods[ribbon.id] ?? {});
    let resultUrl: string | undefined;
    let overlayUrl: string | undefined;
    for (const mod of modifiers) {
      if (mod.type === 'select' && mod.options) {
        const sel = mods[mod.name] ?? mod.options[0]?.value;
        if (sel) { const opt = mod.options.find(o => o.value === sel); if (opt?.url) resultUrl = opt.url; if (opt?.overlayUrl) overlayUrl = opt.overlayUrl; }
      }
      if (mod.type === 'checkbox' && mod.affectsImage && mods[mod.name] === '1') resultUrl = mod.variantUrl;
    }
    return { url: resultUrl, overlayUrl };
  };

  const setPreviewMod = (ribbonId: string, modName: string, value: string) => {
    setPreviewMods(prev => ({ ...prev, [ribbonId]: { ...(prev[ribbonId] ?? {}), [modName]: value } }));
    if (barIds.includes(ribbonId)) setMod(ribbonId, modName, value);
  };

  const setMod = (ribbonId: string, modName: string, value: string) => {
    setBarMods(prev => ({
      ...prev,
      [ribbonId]: { ...(prev[ribbonId] ?? {}), [modName]: value }
    }));
  };

  const clearMod = (ribbonId: string, modName: string) => {
    setBarMods(prev => {
      const updated = { ...(prev[ribbonId] ?? {}) };
      delete updated[modName];
      return { ...prev, [ribbonId]: updated };
    });
  };

  const barRibbons = barIds.map(id => allRibbons.find(r => r.id === id)).filter(Boolean);



  const totalPages = Math.ceil(filteredRibbons.length / RIBBONS_PER_PAGE);
  const pagedRibbons = filteredRibbons.slice((ribbonPage - 1) * RIBBONS_PER_PAGE, ribbonPage * RIBBONS_PER_PAGE);

  // Reset to page 1 when search/filter changes
  const handleRibbonSearch = (v: string) => { setRibbonSearch(v); setRibbonPage(1); };
  const handleRibbonCountry = (v: string) => { setRibbonCountry(v); setRibbonPage(1); };

  // Chunk bar into rows of 5
  const rows: any[][] = [];
  for (let i = 0; i < barRibbons.length; i += 5) rows.push(barRibbons.slice(i, i + 5));

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Rows3 className="w-5 h-5 text-primary" />
            <h2 className="font-display font-black text-lg uppercase tracking-widest">Ribbon Bar</h2>
            <span className="text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 rounded flex items-center gap-1">
              <Crown className="w-2.5 h-2.5" /> Pro
            </span>
          </div>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">
            Build your service ribbon rack from awards granted by your unit. Drag to reorder.
          </p>
        </div>
        <button
          onClick={saveBar}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded font-display text-xs uppercase tracking-widest hover:bg-primary/90 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save Bar
        </button>
      </div>

      {/* Live Ribbon Rack Preview */}
      <div className="border border-primary/30 rounded-lg overflow-hidden">
        <div className="bg-primary/10 border-b border-primary/20 px-5 py-3 flex items-center gap-2">
          <p className="font-display font-black text-xs uppercase tracking-widest text-primary">Your Ribbon Rack</p>
          <span className="text-xs text-muted-foreground font-sans">— {barRibbons.length} ribbon{barRibbons.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="p-5 bg-card min-h-[80px]">
          {barRibbons.length === 0 ? (
            <div className="text-center py-6 border border-dashed border-border rounded-lg">
              <Rows3 className="w-8 h-8 mx-auto mb-2 opacity-20" />
              <p className="text-xs font-sans text-muted-foreground">No ribbons in your rack yet — select from your awards below</p>
            </div>
          ) : (
            <div className="space-y-0.5 inline-block">
              {rows.map((row, ri) => (
                <div key={ri} className="flex gap-0.5">
                  {row.map((ribbon: any) => (
                    <div
                      key={ribbon.id}
                      draggable
                      onDragStart={() => handleDragStart(ribbon.id)}
                      onDragEnd={handleDragEnd}
                      onDragOver={e => handleDragOver(e, ribbon.id)}
                      onDrop={() => handleDrop(ribbon.id)}
                      className={`relative cursor-grab active:cursor-grabbing transition-all ${
                        dragging === ribbon.id ? "opacity-40" : ""
                      } ${dragOver === ribbon.id ? "ring-2 ring-primary" : ""}`}
                      title={ribbon.award_name ?? ribbon.name ?? ""}
                      onMouseEnter={() => setHovered(ribbon.id)}
                      onMouseLeave={() => setHovered(null)}
                    >
                      {(() => { const r = getModifierResult(ribbon, true); return <RibbonImage award={ribbon} size={52} modifierUrl={r.url} overlayUrl={r.overlayUrl} />; })()}
                      {hovered === ribbon.id && (
                        <button
                          onClick={() => toggleInBar(ribbon.id)}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[9px] flex items-center justify-center hover:bg-red-600 z-10"
                          title="Remove from rack"
                        >×</button>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Available Awards */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="bg-secondary/40 border-b border-border px-5 py-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <p className="font-display font-black text-xs uppercase tracking-widest">Awarded Ribbons — Select to Add</p>
            <span className="text-xs text-muted-foreground font-sans">({allRibbons.length} total granted)</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              placeholder="Search ribbons..."
              value={ribbonSearch}
              onChange={e => handleRibbonSearch(e.target.value)}
              className="text-xs bg-background border border-border rounded px-2 py-1 font-sans w-40 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {ribbonCountries.length > 0 && (
              <select
                value={ribbonCountry}
                onChange={e => handleRibbonCountry(e.target.value)}
                className="text-xs bg-background border border-border rounded px-2 py-1 font-sans focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="all">All Countries</option>
                {ribbonCountries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
        </div>
        <div className="p-5">
          {allRibbons.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-border rounded-lg">
              <Award className="w-8 h-8 mx-auto mb-2 opacity-20" />
              <p className="text-xs font-sans text-muted-foreground">No ribbons have been awarded to you yet</p>
              <p className="text-[10px] text-muted-foreground/60 font-sans mt-1">Your commander issues ribbons from the unit's Awards section</p>
            </div>
          ) : (
            <>
            {filteredRibbons.length === 0 && (
              <div className="text-center py-6 border border-dashed border-border rounded-lg mb-4">
                <p className="text-xs font-sans text-muted-foreground">No ribbons match your search</p>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {pagedRibbons.map((ribbon: any) => {
                const inBar = barIds.includes(ribbon.id);
                const baseUrl = ribbonImageUrl(ribbon);
                const modifiers = getRibbonModifiers(baseUrl);
                const hasModifiers = modifiers.length > 0;
                const activeMods = inBar
                  ? { ...(previewMods[ribbon.id] ?? {}), ...(barMods[ribbon.id] ?? {}) }
                  : (previewMods[ribbon.id] ?? {});
                const modResult = getModifierResult(ribbon, true);
                const modUrl = modResult.url;
                return (
                  <div
                    key={ribbon.id}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${
                      inBar
                        ? "border-primary/60 bg-primary/10 ring-1 ring-primary/30"
                        : "border-border bg-card hover:border-primary/40 hover:bg-primary/5"
                    }`}
                  >
                    <button className="flex flex-col items-center gap-2 w-full" onClick={() => toggleInBar(ribbon.id)}>
                      <RibbonImage award={ribbon} size={44} modifierUrl={modUrl} overlayUrl={getModifierResult(ribbon, true).overlayUrl} />
                      <div className="text-center">
                        <p className="text-[10px] font-display font-bold uppercase tracking-wider leading-tight line-clamp-2">
                          {ribbon.award_name ?? ribbon.name ?? "Ribbon"}
                        </p>
                        {ribbon.reason && (
                          <p className="text-[9px] text-muted-foreground font-sans mt-0.5 line-clamp-1">{ribbon.reason}</p>
                        )}
                      </div>
                      <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
                        inBar ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"
                      }`}>
                        {inBar ? "In Rack ✓" : "Add to Rack"}
                      </span>
                    </button>
                    {/* Modifier controls — always visible when ribbon has variants */}
                    {hasModifiers && (
                      <div className={`w-full pt-2 space-y-1.5 border-t ${inBar ? "border-primary/20" : "border-border/60"}`}
                        onClick={e => e.stopPropagation()}>
                        <p className={`text-[8px] font-display font-bold uppercase tracking-widest mb-1 ${inBar ? "text-primary" : "text-muted-foreground"}`}>
                          {inBar ? "Variants & Clasps" : "Preview Grade"}
                        </p>
                        {modifiers.map((mod) => (
                          <div key={mod.name} className="flex flex-col gap-0.5">
                            {mod.type === 'select' && mod.options && (
                              <>
                                <label className="text-[8px] text-muted-foreground font-sans uppercase tracking-wider">{mod.label}</label>
                                <select
                                  value={activeMods[mod.name] ?? mod.options[0].value}
                                  onChange={e => setPreviewMod(ribbon.id, mod.name, e.target.value)}
                                  className="text-[9px] bg-background border border-border rounded px-1 py-0.5 w-full font-sans focus:outline-none focus:ring-1 focus:ring-primary/40"
                                >
                                  {mod.options.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </>
                            )}
                            {mod.type === 'checkbox' && (
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={activeMods[mod.name] === '1'}
                                  onChange={e => setPreviewMod(ribbon.id, mod.name, e.target.checked ? '1' : '')}
                                  className="w-3 h-3 accent-primary"
                                />
                                <span className="text-[9px] text-muted-foreground font-sans">{mod.label}</span>
                              </label>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                <button
                  onClick={() => setRibbonPage(p => Math.max(1, p - 1))}
                  disabled={ribbonPage === 1}
                  className="px-3 py-1 text-xs font-display uppercase tracking-widest border border-border rounded hover:bg-secondary disabled:opacity-40"
                >← Prev</button>
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => (
                    <button
                      key={pg}
                      onClick={() => setRibbonPage(pg)}
                      className={`w-7 h-7 text-xs font-display rounded ${pg === ribbonPage ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}
                    >{pg}</button>
                  ))}
                </div>
                <button
                  onClick={() => setRibbonPage(p => Math.min(totalPages, p + 1))}
                  disabled={ribbonPage === totalPages}
                  className="px-3 py-1 text-xs font-display uppercase tracking-widest border border-border rounded hover:bg-secondary disabled:opacity-40"
                >Next →</button>
              </div>
            )}
            </>
          )}
        </div>
      </div>

      {/* Info box */}
      <div className="flex items-start gap-3 p-4 bg-secondary/30 border border-border rounded-lg">
        <Info className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
        <div className="text-xs text-muted-foreground font-sans space-y-1">
          <p><strong className="text-foreground">Ribbon images</strong> — Ribbons are sourced from an international medals database covering UK, Commonwealth, NATO and allied nations. Your unit's custom ribbons use the image uploaded by your commander.</p>
          <p><strong className="text-foreground">Adding ribbons</strong> — Click any awarded ribbon below to toggle it into your rack. Only awards granted to you by your unit appear here.</p>
          <p><strong className="text-foreground">Drag to reorder</strong> — Drag ribbons within the rack preview to set your precedence order. Rows fill 3 per line, left to right.</p>
          <p><strong className="text-foreground">Saving</strong> — Click Save Bar to persist your arrangement. It will be visible on your public profile.</p>
        </div>
      </div>
    </div>
  );
}

/* ─── Member Loadouts Tab ──────────────────────────────────────────────────── */
const MEMBER_API_BASE = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions";

/* ─── Unit Service Files Tab (read-only) ─────────────────────────────────── */
function UnitServiceFilesTab({ group, user }: any) {
  const [roster, setRoster]   = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [repData, setRepData] = useState<Record<string, any>>({});
  const [loadingRoster, setLoadingRoster] = useState(true);
  const [loadingRep, setLoadingRep]       = useState(false);

  useEffect(() => {
    apiFetch<any>(`/milsimGroups?path=${group.id}/full`)
      .then((g: any) => {
        const ranks: any[] = g.ranks ?? [];
        const enriched = (g.roster ?? [])
          .filter((m: any) => ['Active','active'].includes(m.status ?? ''))
          .map((m: any) => ({
            ...m,
            userId: m.userId ?? m.user_id ?? null,
            rankName: m.rankName ?? m.rank_name ?? ranks.find((r: any) => r.id === (m.rank_id ?? m.rankId))?.name ?? null,
          }));
        setRoster(enriched);
      })
      .catch(() => {})
      .finally(() => setLoadingRoster(false));
  }, [group.id]);

  const loadRep = async (member: any) => {
    if (selected?.userId === member.userId && repData[member.userId]) return;
    setSelected(member);
    if (repData[member.userId]) return;
    setLoadingRep(true);
    try {
      const data = await apiFetch<any>(`/reputation?path=${member.userId}`);
      setRepData(prev => ({ ...prev, [member.userId]: data }));
    } catch {}
    setLoadingRep(false);
  };

  const GRADE_COLORS: Record<string, string> = {
    ELITE: "#fbbf24", TRUSTED: "#4ade80", STANDARD: "#60a5fa",
    CAUTION: "#fb923c", "HIGH RISK": "#f87171", BLACKLISTED: "#ef4444", UNRATED: "#6b7280",
  };

  const rep    = selected ? repData[selected.userId] ?? null : null;
  const score  = rep?.score;

  if (loadingRoster) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (roster.length === 0) return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg text-muted-foreground">
      <Users className="w-10 h-10 mx-auto mb-3 opacity-20" />
      <p className="font-display text-sm uppercase tracking-widest">No Active Roster</p>
    </div>
  );

  return (
    <div className="flex gap-5 min-h-[500px]">
      {/* Roster list */}
      <div className="shrink-0 w-48 space-y-1">
        <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Select Operator</p>
        {roster.map((m: any) => {
          const isMe = m.userId === (user as any)?.id;
          const isSelected = selected?.userId === m.userId;
          return (
            <button
              key={m.id}
              onClick={() => loadRep(m)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded text-left transition-colors ${
                isSelected
                  ? "bg-primary/15 border border-primary/30 text-primary"
                  : "border border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`}
            >
              <div className="w-6 h-6 rounded bg-secondary border border-border flex items-center justify-center shrink-0">
                <span className="text-[9px] font-bold text-muted-foreground">{(m.callsign ?? "?")[0].toUpperCase()}</span>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-display font-bold uppercase tracking-widest truncate">{m.callsign ?? "Unknown"}</p>
                {m.rankName && <p className="text-[9px] text-muted-foreground truncate">{m.rankName}</p>}
              </div>
              {isMe && <span className="ml-auto text-[8px] font-bold uppercase tracking-widest text-primary/70 shrink-0">You</span>}
            </button>
          );
        })}
      </div>

      {/* Service file panel */}
      <div className="flex-1 min-w-0">
        {!selected && (
          <div className="flex flex-col items-center justify-center h-full py-16 text-muted-foreground gap-3 border border-dashed border-border rounded-lg">
            <FileText className="w-10 h-10 opacity-20" />
            <p className="text-xs font-display uppercase tracking-widest">Select an operator to view their service file</p>
          </div>
        )}

        {selected && (
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center gap-3 pb-3 border-b border-border">
              <div className="w-10 h-10 rounded bg-primary/10 border border-primary/20 flex items-center justify-center">
                <span className="font-black text-primary text-base">{(selected.callsign ?? "?")[0].toUpperCase()}</span>
              </div>
              <div>
                <p className="font-display font-black text-base uppercase tracking-widest">{selected.callsign}</p>
                <p className="text-xs text-muted-foreground">{selected.rankName ?? "No rank"} · {selected.status ?? "Active"}</p>
              </div>
              <div className="ml-auto">
                <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded border bg-secondary border-border text-muted-foreground">Read Only</span>
              </div>
            </div>

            {loadingRep && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}

            {!loadingRep && repData[selected.userId] !== undefined && (
              <>
                {!score || score.grade === "UNRATED" ? (
                  <div className="text-center py-12 border border-dashed border-border rounded-lg text-muted-foreground">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-20" />
                    <p className="text-xs font-display uppercase tracking-widest">No reviews on file</p>
                    <p className="text-[11px] font-sans mt-1 opacity-60">This operator hasn't been reviewed yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Score card */}
                    <div className="bg-card border border-border rounded-lg p-5 flex items-center gap-5">
                      <div className="text-center shrink-0">
                        <p className="text-5xl font-black" style={{ color: GRADE_COLORS[score.grade] ?? "#6b7280" }}>{score.overall}</p>
                        <p className="text-[10px] font-bold uppercase tracking-widest mt-0.5" style={{ color: GRADE_COLORS[score.grade] ?? "#6b7280" }}>{score.grade}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="grid grid-cols-2 gap-2.5">
                          {[
                            { label: "Activity",   value: score.activity },
                            { label: "Attitude",   value: score.attitude },
                            { label: "Experience", value: score.experience },
                            { label: "Discipline", value: score.discipline },
                          ].map(s => (
                            <div key={s.label}>
                              <div className="flex justify-between text-[10px] mb-1">
                                <span className="text-muted-foreground font-display uppercase tracking-wider">{s.label}</span>
                                <span className="font-bold text-foreground">{s.value?.toFixed(1) ?? "—"}</span>
                              </div>
                              <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                                <div className="h-full bg-primary rounded-full" style={{ width: `${((s.value ?? 0) / 10) * 100}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground font-sans mt-2">{rep.review_count} review{rep.review_count !== 1 ? "s" : ""} on file</p>
                      </div>
                    </div>

                    {/* Blacklist warning */}
                    {score.grade === "BLACKLISTED" && (
                      <div className="flex items-start gap-2 p-3 rounded border border-red-500/40 bg-red-500/10 text-red-400 text-xs font-sans">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>This operator has been flagged across one or more units.</span>
                      </div>
                    )}

                    {/* Reviews */}
                    {Array.isArray(rep.reviews) && rep.reviews.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Reviews on File</p>
                        {rep.reviews.map((r: any, i: number) => (
                          <div key={i} className="bg-card border border-border rounded-lg p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-display font-bold text-[11px] uppercase tracking-widest text-foreground">{r.group_name ?? "Anonymous"}</p>
                                {r.notes && <p className="text-xs text-muted-foreground font-sans mt-1 leading-relaxed">{r.notes}</p>}
                              </div>
                              <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border shrink-0 ${
                                r.overall_vote === "commend" ? "text-green-400 border-green-500/30 bg-green-500/10" :
                                r.overall_vote === "caution" ? "text-amber-400 border-amber-500/30 bg-amber-500/10" :
                                "text-muted-foreground border-border"
                              }`}>{r.overall_vote}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MemberLoadoutsTab({ group, groupDetail, rosterEntry }: any) {
  const [kits, setKits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const token = localStorage.getItem("tag_auth_token") ?? "";
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  // Resolve member's role name using groupDetail.roles (full data) with multiple field fallbacks
  const memberRoleName: string | null = (() => {
    const rid = rosterEntry?.role_id ?? rosterEntry?.roleId ?? rosterEntry?.role?.id;
    if (!rid) return rosterEntry?.roleName ?? rosterEntry?.role_name ?? null;
    const allRoles = groupDetail?.roles ?? group?.roles ?? [];
    const role = allRoles.find((r: any) => r.id === rid || r.id === String(rid) || String(r.id) === String(rid));
    return role?.name ?? rosterEntry?.roleName ?? rosterEntry?.role_name ?? null;
  })();

  useEffect(() => {
    fetch(`${MEMBER_API_BASE}/loadoutKits?group_id=${group.id}`, { headers })
      .then(r => r.json())
      .then(d => {
        const all: any[] = Array.isArray(d) ? d : [];
        // Filter: show kits with no role tags (universal), or kits whose tags include member's role
        const visible = all.filter((k: any) => {
          if (!k.is_active) return false;
          const tags: string[] = k.role_tags ?? [];
          if (tags.length === 0) return true; // universal kit
          if (!memberRoleName) return true; // no role assigned — show all
          return tags.some((t: string) => t.toLowerCase() === memberRoleName.toLowerCase());
        });
        setKits(visible);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [group.id, memberRoleName]);

  const KIT_CATEGORIES = ["Uniform","Vest / Carrier","Primary Weapon","Secondary Weapon","Ammunition","Optics","Communications","Medical","Equipment","Rations","Other"];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display font-black uppercase tracking-widest text-base text-foreground">Loadout Kits</h3>
        <p className="text-xs text-muted-foreground font-sans mt-0.5">
          {memberRoleName
            ? <>Showing kits for your role: <span className="text-primary font-bold">{memberRoleName}</span> + universal kits</>
            : "Showing all unit loadout kits"}
        </p>
      </div>

      {loading && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}

      {!loading && kits.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 border border-dashed border-border rounded-lg text-center">
          <Package className="w-10 h-10 text-muted-foreground/30" />
          <p className="text-sm font-display font-bold uppercase tracking-wider text-muted-foreground">No loadout kits assigned</p>
          <p className="text-xs text-muted-foreground/60 font-sans">Your commanders haven't posted any kits for your role yet</p>
        </div>
      )}

      <div className="space-y-2">
        {kits.map((kit: any) => (
          <div key={kit.id} className="border border-border rounded-lg overflow-hidden">
            <div
              className="flex items-center gap-3 px-4 py-3 bg-secondary/30 cursor-pointer"
              onClick={() => setExpanded(expanded === kit.id ? null : kit.id)}
            >
              <Package className="w-4 h-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold uppercase tracking-widest text-xs text-foreground">{kit.name}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(kit.role_tags ?? []).map((t: string) => (
                    <span key={t} className="text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-primary/10 border border-primary/20 text-primary">{t}</span>
                  ))}
                  {(kit.role_tags ?? []).length === 0 && (
                    <span className="text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-secondary border border-border text-muted-foreground">All Roles</span>
                  )}
                  {kit.game && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-secondary border border-border text-muted-foreground">{kit.game}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] text-muted-foreground font-mono">{(kit.items ?? []).length} items</span>
                {expanded === kit.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </div>
            </div>

            {expanded === kit.id && (
              <div className="px-4 pb-4 pt-3 space-y-3">
                {kit.description && (
                  <p className="text-xs font-sans text-muted-foreground italic border-l-2 border-primary/30 pl-3">{kit.description}</p>
                )}
                {KIT_CATEGORIES.filter(cat => (kit.items ?? []).some((i: any) => i.category === cat)).map(cat => (
                  <div key={cat}>
                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">{cat}</p>
                    <div className="space-y-1">
                      {(kit.items ?? []).filter((i: any) => i.category === cat).map((item: any, idx: number) => (
                        <div key={idx} className="flex items-start gap-3 px-3 py-2 rounded bg-secondary/40 border border-border/50">
                          {item.mandatory
                            ? <CheckSquare className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                            : <Square className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0 mt-0.5" />}
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-sans text-foreground">{item.name}</span>
                            {item.quantity && <span className="ml-2 text-[10px] font-mono text-muted-foreground">×{item.quantity}</span>}
                            {item.notes && <p className="text-[10px] text-muted-foreground/70 mt-0.5">{item.notes}</p>}
                          </div>
                          {item.mandatory && <span className="text-[9px] font-display uppercase tracking-widest text-primary/70 shrink-0">Required</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {(kit.items ?? []).length === 0 && (
                  <p className="text-xs text-muted-foreground/50 italic">No items in this kit yet</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Member Servers Tab ───────────────────────────────────────────────────── */
function MemberServersTab({ group }: any) {
  const [servers, setServers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState<string | null>(null);
  const token = localStorage.getItem("tag_auth_token") ?? "";
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${MEMBER_API_BASE}/gameServers?group_id=${group.id}`, { headers })
      .then(r => r.json())
      .then(d => { setServers(Array.isArray(d) ? d.filter((s: any) => s.is_active !== false) : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [group.id]);

  const toggleReveal = (id: string) => setRevealed(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const copyText = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1800);
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display font-black uppercase tracking-widest text-base text-foreground">Game Servers</h3>
        <p className="text-xs text-muted-foreground font-sans mt-0.5">Your unit's active servers — connection details and passwords</p>
      </div>

      {loading && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}

      {!loading && servers.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 border border-dashed border-border rounded-lg text-center">
          <Server className="w-10 h-10 text-muted-foreground/30" />
          <p className="text-sm font-display font-bold uppercase tracking-wider text-muted-foreground">No servers configured</p>
          <p className="text-xs text-muted-foreground/60 font-sans">Your commanders haven't added any game servers yet</p>
        </div>
      )}

      <div className="space-y-3">
        {servers.map((srv: any) => {
          const isRevealed = revealed.has(srv.id);
          const addrKey = `addr-${srv.id}`;
          const passKey = `pass-${srv.id}`;
          return (
            <div key={srv.id} className={`border rounded-lg overflow-hidden ${srv.is_primary ? "border-primary/40" : "border-border"}`}>
              {/* Header */}
              <div className="flex items-center gap-3 px-4 py-3 bg-secondary/20">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-bold uppercase tracking-widest text-xs text-foreground">{srv.label}</p>
                    {srv.is_primary && (
                      <span className="text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/15 border border-primary/30 text-primary">Primary</span>
                    )}
                  </div>
                  {srv.game && <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{srv.game}</p>}
                </div>
              </div>

              {/* Details */}
              <div className="px-4 py-3 space-y-3 border-t border-border/50">
                {srv.server_address && (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-0.5">Server Address</p>
                      <p className="text-sm font-mono text-foreground">{srv.server_address}{srv.port ? `:${srv.port}` : ""}</p>
                    </div>
                    <button
                      onClick={() => copyText(`${srv.server_address}${srv.port ? `:${srv.port}` : ""}`, addrKey)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-border hover:bg-secondary text-xs font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all"
                    >
                      {copied === addrKey ? <CheckCircle2 className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                      {copied === addrKey ? "Copied" : "Copy"}
                    </button>
                  </div>
                )}

                {srv.password && (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-0.5">
                        <Lock className="w-2.5 h-2.5 inline mr-1" />Password
                      </p>
                      <p className="text-sm font-mono text-foreground tracking-widest">{isRevealed ? srv.password : "••••••••••"}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => toggleReveal(srv.id)}
                        className="p-1.5 rounded border border-border hover:bg-secondary text-muted-foreground hover:text-foreground transition-all"
                      >
                        {isRevealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={() => copyText(srv.password, passKey)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-border hover:bg-secondary text-xs font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all"
                      >
                        {copied === passKey ? <CheckCircle2 className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                        {copied === passKey ? "Copied" : "Copy"}
                      </button>
                    </div>
                  </div>
                )}

                {srv.modset_url && (
                  <div>
                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-0.5">Modset / Preset</p>
                    <a
                      href={srv.modset_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline font-mono"
                    >
                      <ExternalLink className="w-3 h-3" /> Open Modset Link
                    </a>
                  </div>
                )}

                {srv.notes && (
                  <div className="pt-2 border-t border-border/40">
                    <p className="text-xs font-sans text-muted-foreground italic">{srv.notes}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Member Discharge Papers Tab ───────────────────────────────────────── */
function MemberDischargeTab({ group, user, rosterEntry, groupDetail }: any) {
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    if (!group?.id) return;
    setLoading(true);
    apiFetch<any[]>(`/api/member-discharge?path=${group.id}`)
      .then(r => { setRecords(r ?? []); setError(null); })
      .catch(e => setError(e.message ?? "Failed to load records"))
      .finally(() => setLoading(false));
  }, [group?.id]);

  const typeColor: Record<string, string> = {
    Honourable:    "bg-green-500/10 text-green-400 border-green-500/30",
    Dishonourable: "bg-red-500/10 text-red-400 border-red-500/30",
    Medical:       "bg-blue-500/10 text-blue-400 border-blue-500/30",
    Voluntary:     "bg-purple-500/10 text-purple-400 border-purple-500/30",
    Administrative:"bg-secondary text-muted-foreground border-border",
  };

  // Resolve current rank/role for header card
  const ranks: any[] = groupDetail?.ranks ?? [];
  const roles: any[] = groupDetail?.roles ?? [];
  const currentRank = rosterEntry?.rankId ? ranks.find((r: any) => r.id === rosterEntry.rankId) : null;
  const currentRole = rosterEntry?.roleId ? roles.find((r: any) => r.id === rosterEntry.roleId) : null;
  const currentRankName = currentRank?.name ?? rosterEntry?.rank_name ?? null;
  const currentRankAbbr = currentRank?.abbreviation ?? null;
  const currentRoleName = currentRole?.name ?? rosterEntry?.roleName ?? null;

  if (loading) return (
    <div className="flex justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  );

  if (error) return (
    <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm font-sans">
      <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
    </div>
  );

  if (records.length === 0) return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg">
      <UserMinus2 className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
      <p className="font-display font-bold uppercase tracking-widest text-sm text-muted-foreground">No Discharge Records</p>
      <p className="text-xs text-muted-foreground/60 font-sans mt-1">You have no discharge paperwork on file for {group.name}.</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header notice */}
      <div className="flex items-start gap-3 p-3.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
        <Info className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
        <p className="text-xs font-sans text-amber-200/80">
          These are your official discharge records on file with <span className="font-bold text-amber-300">{group.name}</span>. This is a read-only view — contact your unit commander if you believe any information is incorrect.
        </p>
      </div>

      {/* Current rank/role card — shown even when there are no discharge records */}
      {rosterEntry && (currentRankName || currentRoleName || rosterEntry.callsign) && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-primary" />
            <h3 className="font-display font-bold uppercase tracking-widest text-xs text-foreground">Current Rank &amp; Assignment</h3>
            <span className="ml-auto text-[9px] font-display uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5 rounded">Read Only</span>
          </div>
          <div className="px-5 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {rosterEntry.callsign && (
                <div className="bg-secondary/40 border border-border rounded p-2.5">
                  <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">Callsign</p>
                  <p className="text-sm font-display font-black text-primary">{rosterEntry.callsign}</p>
                </div>
              )}
              {currentRankName && (
                <div className="bg-secondary/40 border border-border rounded p-2.5">
                  <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">Rank</p>
                  <p className="text-sm font-display font-bold text-foreground">{currentRankAbbr ? `${currentRankAbbr} — ${currentRankName}` : currentRankName}</p>
                </div>
              )}
              {currentRoleName && (
                <div className="bg-secondary/40 border border-border rounded p-2.5">
                  <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">Role / Position</p>
                  <p className="text-sm font-display font-bold text-foreground">{currentRoleName}</p>
                </div>
              )}
              {rosterEntry.status && (
                <div className="bg-secondary/40 border border-border rounded p-2.5">
                  <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">Status</p>
                  <p className={`text-sm font-display font-bold capitalize ${
                    rosterEntry.status === "active" ? "text-green-400" :
                    rosterEntry.status === "reserve" ? "text-amber-400" :
                    rosterEntry.status === "loa" ? "text-blue-400" :
                    "text-muted-foreground"
                  }`}>{rosterEntry.status}</p>
                </div>
              )}
            </div>
            {rosterEntry.specialisations && rosterEntry.specialisations.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border/40">
                <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-2">Specialisations</p>
                <div className="flex flex-wrap gap-1.5">
                  {(Array.isArray(rosterEntry.specialisations) ? rosterEntry.specialisations : [rosterEntry.specialisations]).map((s: string, i: number) => (
                    <span key={i} className="text-[10px] font-display uppercase tracking-wider bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 rounded">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Records */}
      {records.map((d: any) => (
        <div key={d.id} className="bg-card border border-border rounded-xl overflow-hidden">
          {/* Header stripe */}
          <div className={`px-5 py-3 border-b flex items-center justify-between flex-wrap gap-2 ${
            d.discharge_type === "Dishonourable" ? "border-red-500/20 bg-red-500/5" :
            d.discharge_type === "Honourable"    ? "border-green-500/20 bg-green-500/5" :
            "border-border bg-secondary/30"
          }`}>
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="font-display font-black uppercase tracking-widest text-base text-foreground">
                {d.callsign}
              </span>
              <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-2 py-1 rounded border ${typeColor[d.discharge_type] ?? typeColor.Administrative}`}>
                {d.discharge_type} Discharge
              </span>
              {d.reinstatement_eligible === false && (
                <span className="text-[9px] font-display font-bold uppercase tracking-widest text-red-400 border border-red-400/30 bg-red-400/10 px-2 py-1 rounded">
                  No Reinstatement
                </span>
              )}
            </div>
            <span className="text-xs text-muted-foreground font-sans">
              {d.effective_date ?? d.created_date?.slice(0,10) ?? "—"}
            </span>
          </div>

          {/* Body */}
          <div className="px-5 py-4 space-y-4">
            {/* Reason */}
            <div>
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Reason</p>
              <p className="text-sm font-sans text-foreground/90 leading-relaxed">{d.reason}</p>
            </div>

            {/* Meta row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {d.final_rank && (
                <div className="bg-secondary/40 border border-border rounded p-2.5">
                  <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">Final Rank</p>
                  <p className="text-xs font-display font-bold text-primary">{d.final_rank}</p>
                </div>
              )}
              {d.final_role && (
                <div className="bg-secondary/40 border border-border rounded p-2.5">
                  <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">Final Role</p>
                  <p className="text-xs font-display font-bold text-foreground">{d.final_role}</p>
                </div>
              )}
              {d.ops_served !== undefined && d.ops_served !== null && (
                <div className="bg-secondary/40 border border-border rounded p-2.5">
                  <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">Ops Served</p>
                  <p className="text-xs font-display font-bold text-foreground">{d.ops_served}</p>
                </div>
              )}
              {d.join_date && (
                <div className="bg-secondary/40 border border-border rounded p-2.5">
                  <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">Enlistment Date</p>
                  <p className="text-xs font-display font-bold text-foreground">{d.join_date}</p>
                </div>
              )}
            </div>

            {/* Notes */}
            {d.notes && (
              <div className="bg-secondary/30 border border-border rounded p-3">
                <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-1">Commander Notes</p>
                <p className="text-xs font-sans text-foreground/80 italic leading-relaxed">{d.notes}</p>
              </div>
            )}

            {/* Footer — conducted by */}
            <div className="pt-2 border-t border-border/40 flex items-center justify-between text-[10px] text-muted-foreground font-sans flex-wrap gap-1">
              <span>Conducted by: <span className="text-foreground font-semibold">{d.conducted_by_username ?? d.conducted_by ?? "—"}</span></span>
              <span>Unit: <span className="text-foreground font-semibold">{d.group_name ?? group.name}</span></span>
              {d.created_date && <span>Filed: {new Date(d.created_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Training Reviews tab ────────────────────────────────────────────────── */
function MemberTrainingReviewsTab({ group, showMsg, user }: any) {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ops, setOps] = useState<any[]>([]);
  const BLANK = {
    exercise_name: "", op_id: "", exercise_date: "",
    conducted_by: (user as any)?.username ?? "",
    participant_count: "", skills_practised: "",
    overall_execution: 3, comms_quality: 3, tactics_quality: 3, discipline_quality: 3,
    notable_gaps: "", highlights: "", repeat_recommended: false,
  };
  const [form, setForm] = useState<any>(BLANK);

  useEffect(() => {
    if (!group?.id) return;
    setLoading(true);
    apiFetch<any>(`/milsimTrainingReviews?group_id=${group.id}`)
      .then((r: any) => setReviews(Array.isArray(r) ? r : r?.items ?? []))
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
    apiFetch<any[]>(`/milsimOps?path=group/${group.id}`)
      .then(d => setOps((d ?? []).filter((o: any) => o.event_type === "training" || o.event_type === "Training")))
      .catch(() => {});
  }, [group?.id]);

  const sorted = [...reviews].sort((a, b) =>
    new Date(b.exercise_date ?? b.created_date ?? 0).getTime() -
    new Date(a.exercise_date ?? a.created_date ?? 0).getTime()
  );

  const handleSubmit = async () => {
    if (!form.exercise_name.trim()) { showMsg("Exercise name is required", "error"); return; }
    setSubmitting(true);
    try {
      await apiFetch(`/milsimTrainingReviews`, {
        method: "POST",
        body: JSON.stringify({
          group_id: group.id,
          ...form,
          reviewed_by: (user as any)?.username ?? "",
          participant_count: form.participant_count ? parseInt(form.participant_count) : undefined,
        }),
      });
      showMsg("Training review submitted", "success");
      setShowForm(false);
      setForm(BLANK);
      // Reload
      apiFetch<any>(`/milsimTrainingReviews?group_id=${group.id}`)
        .then((r: any) => setReviews(Array.isArray(r) ? r : r?.items ?? []))
        .catch(() => {});
    } catch { showMsg("Failed to submit review", "error"); }
    setSubmitting(false);
  };

  const scoreLabel = (n: number) => {
    if (n >= 8) return { t: "Excellent", c: "text-green-400 border-green-500/30 bg-green-500/10" };
    if (n >= 6) return { t: "Good", c: "text-yellow-400 border-yellow-500/30 bg-yellow-500/10" };
    if (n >= 4) return { t: "Average", c: "text-orange-400 border-orange-500/30 bg-orange-500/10" };
    return { t: "Needs Work", c: "text-red-400 border-red-500/30 bg-red-500/10" };
  };

  const bars = [
    { label: "Comms",     key: "comms_quality" },
    { label: "Tactics",   key: "tactics_quality" },
    { label: "Execution", key: "overall_execution" },
    { label: "Discipline",key: "discipline_quality" },
  ];

  const inp = "w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans focus:outline-none focus:border-primary/60";
  const lbl = "block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest">Training Reviews</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Post-exercise assessments</p>
        </div>
        <button
          onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-widest text-primary hover:text-primary/80 border border-primary/30 bg-primary/5 hover:bg-primary/10 rounded px-2.5 py-1.5 transition-all">
          <Brain className="w-3 h-3" />
          {showForm ? "Cancel" : "File Review"}
        </button>
      </div>

      {/* Inline form */}
      {showForm && (
        <div className="border border-primary/30 bg-primary/5 rounded-lg p-5 space-y-4">
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-primary mb-2">New Training Review</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Exercise Name *</label>
              <input className={inp} value={form.exercise_name} onChange={e => setForm((f: any) => ({...f, exercise_name: e.target.value}))} placeholder="e.g. Section Assault Drill" />
            </div>
            <div>
              <label className={lbl}>Exercise Date</label>
              <input type="date" className={inp} value={form.exercise_date} onChange={e => setForm((f: any) => ({...f, exercise_date: e.target.value}))} />
            </div>
            <div>
              <label className={lbl}>Conducted By</label>
              <input className={inp} value={form.conducted_by} onChange={e => setForm((f: any) => ({...f, conducted_by: e.target.value}))} placeholder="Callsign" />
            </div>
            <div>
              <label className={lbl}>Participant Count</label>
              <input type="number" className={inp} value={form.participant_count} onChange={e => setForm((f: any) => ({...f, participant_count: e.target.value}))} placeholder="e.g. 8" />
            </div>
            {ops.length > 0 && (
              <div className="sm:col-span-2">
                <label className={lbl}>Linked Op (optional)</label>
                <select className={inp} value={form.op_id} onChange={e => setForm((f: any) => ({...f, op_id: e.target.value}))}>
                  <option value="">— None —</option>
                  {ops.map((o: any) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            )}
            <div className="sm:col-span-2">
              <label className={lbl}>Skills Practised (comma-separated)</label>
              <input className={inp} value={form.skills_practised} onChange={e => setForm((f: any) => ({...f, skills_practised: e.target.value}))} placeholder="e.g. CQB, Fire & Manoeuvre, Comms" />
            </div>
          </div>
          {/* Quality scores */}
          <div>
            <div className={lbl + " mb-3"}>Quality Scores (1–5)</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { key: "overall_execution", label: "Execution" },
                { key: "comms_quality",     label: "Comms" },
                { key: "tactics_quality",   label: "Tactics" },
                { key: "discipline_quality",label: "Discipline" },
              ].map(f => (
                <div key={f.key}>
                  <label className={lbl}>{f.label}: {form[f.key]}/5</label>
                  <input type="range" min="1" max="5" step="1" value={form[f.key]}
                    onChange={e => setForm((prev: any) => ({...prev, [f.key]: parseInt(e.target.value)}))}
                    className="w-full accent-primary" />
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Highlights</label>
              <textarea rows={3} className={inp} value={form.highlights} onChange={e => setForm((f: any) => ({...f, highlights: e.target.value}))} placeholder="What went well?" />
            </div>
            <div>
              <label className={lbl}>Notable Gaps</label>
              <textarea rows={3} className={inp} value={form.notable_gaps} onChange={e => setForm((f: any) => ({...f, notable_gaps: e.target.value}))} placeholder="What needs work?" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="repeat_rec" checked={form.repeat_recommended} onChange={e => setForm((f: any) => ({...f, repeat_recommended: e.target.checked}))} className="accent-primary" />
            <label htmlFor="repeat_rec" className="text-xs font-sans text-foreground cursor-pointer">Recommend repeating this exercise</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => { setShowForm(false); setForm(BLANK); }} className="px-4 py-2 rounded border border-border text-sm font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
            <button onClick={handleSubmit} disabled={submitting} className="flex items-center gap-2 px-4 py-2 rounded bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-display font-bold uppercase tracking-widest transition-all disabled:opacity-50">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Submit Review
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-14">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Brain className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No training reviews yet</p>
          <p className="text-xs mt-1 text-muted-foreground/70">Reviews are filed by leadership after each exercise</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sorted.map((r: any) => {
            const score = r.ai_score != null ? scoreLabel(r.ai_score) : null;
            return (
              <div key={r.id} className="rounded-lg border border-border/60 bg-card/40 overflow-hidden">
                {/* Top bar */}
                <div className="flex items-center justify-between gap-2 flex-wrap px-4 py-3 border-b border-border/40 bg-secondary/20">
                  <div className="flex items-center gap-2 min-w-0">
                    <Brain className="w-4 h-4 text-primary shrink-0" />
                    <span className="font-display font-black uppercase tracking-wider text-sm truncate">
                      {r.exercise_name ?? "Exercise"}
                    </span>
                    {r.repeat_recommended && (
                      <span className="text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-green-500/30 bg-green-500/10 text-green-400">
                        Repeat ✓
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {score && (
                      <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${score.c}`}>
                        AI: {r.ai_score}/10 — {score.t}
                      </span>
                    )}
                    {r.exercise_date && (
                      <span className="text-[10px] text-muted-foreground font-sans">
                        {new Date(r.exercise_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  {/* Meta row */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground font-sans">
                    {r.conducted_by     && <span>Conducted by: <span className="text-foreground font-semibold">{r.conducted_by}</span></span>}
                    {r.reviewed_by      && <span>Reviewed by: <span className="text-foreground font-semibold">{r.reviewed_by}</span></span>}
                    {r.participant_count && <span>Participants: <span className="text-foreground font-semibold">{r.participant_count}</span></span>}
                  </div>

                  {/* Skills practised */}
                  {r.skills_practised && (
                    <div>
                      <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-1.5">Skills Practised</p>
                      <div className="flex flex-wrap gap-1">
                        {(typeof r.skills_practised === "string"
                          ? r.skills_practised.split(",")
                          : Array.isArray(r.skills_practised) ? r.skills_practised : []
                        ).map((s: string, i: number) => (
                          <span key={i} className="text-[10px] px-2 py-0.5 rounded border border-primary/20 bg-primary/5 text-primary font-display font-bold uppercase tracking-widest">
                            {s.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quality bars */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    {bars.map(b => {
                      const val = r[b.key];
                      if (!val && val !== 0) return null;
                      const pct = Math.round((val / 5) * 100);
                      const col = pct >= 80 ? "bg-green-500" : pct >= 60 ? "bg-yellow-400" : pct >= 40 ? "bg-orange-400" : "bg-red-500";
                      return (
                        <div key={b.key}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[9px] font-display uppercase tracking-widest text-muted-foreground">{b.label}</span>
                            <span className="text-[10px] font-display font-bold text-foreground">{val}/5</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${col}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* AI Summary */}
                  {r.ai_summary && (
                    <div className="bg-primary/5 border border-primary/20 rounded p-3">
                      <p className="text-[9px] font-display uppercase tracking-widest text-primary mb-1">AI Assessment</p>
                      <p className="text-xs font-sans text-foreground/80 italic leading-relaxed">{r.ai_summary}</p>
                    </div>
                  )}

                  {/* Highlights / Gaps */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {r.highlights && (
                      <div className="bg-green-500/5 border border-green-500/20 rounded p-3">
                        <p className="text-[9px] font-display uppercase tracking-widest text-green-400 mb-1">Highlights</p>
                        <p className="text-xs font-sans text-foreground/80 leading-relaxed">{r.highlights}</p>
                      </div>
                    )}
                    {r.notable_gaps && (
                      <div className="bg-red-500/5 border border-red-500/20 rounded p-3">
                        <p className="text-[9px] font-display uppercase tracking-widest text-red-400 mb-1">Notable Gaps</p>
                        <p className="text-xs font-sans text-foreground/80 leading-relaxed">{r.notable_gaps}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── MemberBriefingsTab ────────────────────────────────────────────────────────
function MemberBriefingsTab({ group, showMsg }: any) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const CL: Record<string, string> = {
    unclassified: "text-green-400 border-green-500/30 bg-green-500/10",
    confidential:  "text-blue-400 border-blue-500/30 bg-blue-500/10",
    classified:    "text-yellow-400 border-yellow-500/30 bg-yellow-500/10",
    "top-secret":  "text-red-400 border-red-500/30 bg-red-500/10",
  };
  useEffect(() => {
    if (!group?.id) return;
    apiFetch<any[]>(`/milsimBriefings?path=group/${group.id}`)
      .then(d => setItems((d ?? []).filter((b: any) => b.status === "published")))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.id]);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!items.length) return <div className="text-center py-16 text-muted-foreground font-sans text-sm">No briefings published yet.</div>;
  return (
    <div className="space-y-3">
      {selected ? (
        <div className="border border-border rounded-lg p-5">
          <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4 font-display uppercase tracking-widest">
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${CL[selected.classification] ?? ""}`}>{selected.classification ?? "Unclassified"}</span>
            <span className="text-xs text-muted-foreground font-sans">{selected.op_date ? new Date(selected.op_date).toLocaleDateString() : ""}</span>
          </div>
          <h2 className="font-display font-black text-lg uppercase tracking-wider mb-4">{selected.title}</h2>
          {selected.mission && <div className="mb-3"><div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">Mission</div><p className="text-sm font-sans text-foreground whitespace-pre-wrap">{selected.mission}</p></div>}
          {selected.situation_ground && <div className="mb-3"><div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">Situation</div><p className="text-sm font-sans text-foreground whitespace-pre-wrap">{selected.situation_ground}</p></div>}
          {selected.objectives?.length > 0 && <div className="mb-3"><div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">Objectives</div><ul className="space-y-1">{selected.objectives.map((o: any, i: number) => <li key={i} className="text-sm font-sans text-foreground flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" />{typeof o === "string" ? o : o.text}</li>)}</ul></div>}
          {selected.comms_plan && <div className="mb-3"><div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">Comms Plan</div><p className="text-sm font-sans text-foreground whitespace-pre-wrap">{selected.comms_plan}</p></div>}
          {selected.roe && <div className="mb-3"><div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">ROE</div><p className="text-sm font-sans text-foreground whitespace-pre-wrap">{selected.roe}</p></div>}
          {selected.additional_notes && <div className="mb-3"><div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">Additional Notes</div><p className="text-sm font-sans text-foreground whitespace-pre-wrap">{selected.additional_notes}</p></div>}
        </div>
      ) : (
        items.map((b: any) => (
          <div key={b.id} onClick={() => setSelected(b)} className="flex items-center gap-3 p-4 border border-border/60 rounded-lg hover:border-primary/40 hover:bg-secondary/30 cursor-pointer transition-all">
            <FileText className="w-4 h-4 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm uppercase tracking-wider truncate">{b.title}</div>
              <div className="text-xs text-muted-foreground font-sans mt-0.5">{b.op_date ? new Date(b.op_date).toLocaleDateString() : "No date"}</div>
            </div>
            <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border shrink-0 ${CL[b.classification] ?? "text-muted-foreground border-border"}`}>{b.classification ?? "unclassified"}</span>
          </div>
        ))
      )}
    </div>
  );
}

// ── MemberOpIntelTab ──────────────────────────────────────────────────────────
function MemberOpIntelTab({ group, showMsg }: any) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  useEffect(() => {
    if (!group?.id) return;
    apiFetch<any[]>(`/milsimGroupsB?path=intel-docs&group_id=${group.id}`)
      .then(d => setItems((d ?? []).filter((x: any) => x.is_current !== false)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.id]);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!items.length) return <div className="text-center py-16 text-muted-foreground font-sans text-sm">No intel documents on file.</div>;
  const CL: Record<string, string> = {
    unclassified: "text-green-400 border-green-500/30 bg-green-500/10",
    confidential: "text-blue-400 border-blue-500/30 bg-blue-500/10",
    classified:   "text-yellow-400 border-yellow-500/30 bg-yellow-500/10",
    "top-secret": "text-red-400 border-red-500/30 bg-red-500/10",
  };
  return (
    <div className="space-y-3">
      {selected ? (
        <div className="border border-border rounded-lg p-5">
          <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4 font-display uppercase tracking-widest">
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${CL[selected.classification] ?? ""}`}>{selected.classification}</span>
            <span className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">{selected.doc_type}</span>
          </div>
          <h2 className="font-display font-black text-lg uppercase tracking-wider mb-4">{selected.title}</h2>
          <p className="text-sm font-sans text-foreground whitespace-pre-wrap leading-relaxed">{selected.content}</p>
          {selected.file_url && <a href={selected.file_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-xs text-primary hover:underline font-display uppercase tracking-widest"><ExternalLink className="w-3.5 h-3.5" />Attached File</a>}
        </div>
      ) : (
        items.map((d: any) => (
          <div key={d.id} onClick={() => setSelected(d)} className="flex items-center gap-3 p-4 border border-border/60 rounded-lg hover:border-primary/40 hover:bg-secondary/30 cursor-pointer transition-all">
            <BrainCircuit className="w-4 h-4 text-purple-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm uppercase tracking-wider truncate">{d.title}</div>
              <div className="text-xs text-muted-foreground font-sans mt-0.5">{d.doc_type} · by {d.authored_by_callsign}</div>
            </div>
            <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border shrink-0 ${CL[d.classification] ?? "text-muted-foreground border-border"}`}>{d.classification}</span>
          </div>
        ))
      )}
    </div>
  );
}

// ── MemberCampaignsTab ────────────────────────────────────────────────────────
function MemberCampaignsTab({ group }: any) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!group?.id) return;
    apiFetch<any>(`/campaigns?path=list&group_id=${group.id}`)
      .then(d => setItems(normaliseCampaigns(d)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.id]);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!items.length) return <div className="text-center py-16 text-muted-foreground font-sans text-sm">No campaigns on record.</div>;
  const STATUS: Record<string, string> = {
    planning:  "text-amber-400 bg-amber-500/10 border-amber-500/30",
    active:    "text-green-400 bg-green-500/10 border-green-500/30",
    completed: "text-muted-foreground bg-secondary border-border",
    archived:  "text-muted-foreground bg-secondary/30 border-border",
  };
  return (
    <div className="space-y-3">
      {items.map((c: any) => (
        <div key={c.id} className="border border-border/60 rounded-lg p-4">
          <div className="flex items-start gap-3">
            {c.banner_url && <img src={c.banner_url} alt="" className="w-16 h-16 rounded object-cover shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-display font-black text-sm uppercase tracking-wider">{c.name}</span>
                <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${STATUS[c.status] ?? ""}`}>{c.status}</span>
              </div>
              {c.description && <p className="text-xs font-sans text-muted-foreground mb-2">{c.description}</p>}
              {c.objective && <p className="text-xs font-sans text-foreground"><span className="font-bold text-muted-foreground">Objective:</span> {c.objective}</p>}
              {c.outcome_note && <p className="text-xs font-sans text-green-400 mt-1"><span className="font-bold">Outcome:</span> {c.outcome_note}</p>}
              <div className="flex items-center gap-3 mt-2 text-[10px] font-display uppercase tracking-widest text-muted-foreground">
                {c.start_date && <span>{new Date(c.start_date).toLocaleDateString()}</span>}
                {c.op_ids?.length > 0 && <span>{c.op_ids.length} Op{c.op_ids.length !== 1 ? "s" : ""}</span>}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── MemberDutyRosterTab ───────────────────────────────────────────────────────
function MemberDutyRosterTab({ group, user, rosterEntry }: any) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!group?.id) return;
    apiFetch<any[]>(`/dutyRoster?path=${group.id}`)
      .then(d => setItems((d ?? []).sort((a: any, b: any) => new Date(a.duty_date).getTime() - new Date(b.duty_date).getTime())))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.id]);
  const callsign = rosterEntry?.callsign ?? (user as any)?.username;
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!items.length) return <div className="text-center py-16 text-muted-foreground font-sans text-sm">No duty rosters published.</div>;
  const upcoming = items.filter((r: any) => new Date(r.duty_date) >= new Date());
  const past = items.filter((r: any) => new Date(r.duty_date) < new Date());
  const renderRoster = (list: any[]) => list.map((r: any) => {
    const assignments: any[] = Array.isArray(r.assignments) ? r.assignments : (typeof r.assignments === "string" ? JSON.parse(r.assignments || "[]") : []);
    const mySlots = assignments.filter((a: any) => a.callsign === callsign || a.user_id === (user as any)?.id);
    return (
      <div key={r.id} className={`border rounded-lg p-4 ${mySlots.length > 0 ? "border-primary/40 bg-primary/5" : "border-border/60"}`}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="font-display font-black text-sm uppercase tracking-wider">{r.title}</div>
            <div className="text-xs text-muted-foreground font-sans mt-0.5">{new Date(r.duty_date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
          </div>
          {mySlots.length > 0 && <span className="text-[10px] font-display font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/30 px-2 py-0.5 rounded">You're on duty</span>}
        </div>
        {r.description && <p className="text-xs font-sans text-muted-foreground mb-3">{r.description}</p>}
        {assignments.length > 0 && (
          <div className="space-y-1">
            {assignments.map((a: any, i: number) => (
              <div key={i} className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded ${a.callsign === callsign || a.user_id === (user as any)?.id ? "bg-primary/10 text-primary font-bold" : "text-muted-foreground"}`}>
                <Users className="w-3 h-3 shrink-0" />
                <span className="font-display uppercase tracking-wider">{a.callsign || "Unassigned"}</span>
                {a.role && <span className="text-muted-foreground">— {a.role}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  });
  return (
    <div className="space-y-6">
      {upcoming.length > 0 && (<div><div className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><Calendar className="w-3.5 h-3.5" />Upcoming</div><div className="space-y-3">{renderRoster(upcoming)}</div></div>)}
      {past.length > 0 && (<div><div className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><Clock className="w-3.5 h-3.5" />Past</div><div className="space-y-3 opacity-60">{renderRoster(past)}</div></div>)}
    </div>
  );
}

// ── MemberAwardsTab ───────────────────────────────────────────────────────────
function MemberAwardsTab({ group, user, rosterEntry }: any) {
  const [awards, setAwards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!rosterEntry?.id) return;
    apiFetch<any[]>(`/milsimAwards?path=group/${group.id}`)
      .then(d => setAwards((d ?? []).filter((a: any) => a.recipient_roster_id === rosterEntry.id)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.id, rosterEntry?.id]);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!awards.length) return <div className="text-center py-16 text-muted-foreground font-sans text-sm">No awards on record for your callsign.</div>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {awards.map((a: any) => (
        <div key={a.id} className="flex items-center gap-3 p-4 border border-border/60 rounded-lg bg-secondary/20">
          {a.award_image_url ? <img src={a.award_image_url} alt="" className="w-12 h-12 rounded object-contain shrink-0" /> : <Award className="w-10 h-10 text-yellow-400 shrink-0" />}
          <div>
            <div className="font-display font-bold text-sm uppercase tracking-wider text-foreground">{a.award_name}</div>
            {a.reason && <p className="text-xs font-sans text-muted-foreground mt-1">{a.reason}</p>}
            <div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground mt-1">Awarded by {a.awarded_by}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── MemberConductTab ──────────────────────────────────────────────────────────
function MemberConductTab({ group, user, rosterEntry }: any) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!rosterEntry?.id && !user?.id) return;
    apiFetch<any>(`/milsimConductReport?path=list&group_id=${group.id}`)
      .then((d: any) => setItems(normaliseConductReports(d).filter((c: any) => c.subject_roster_id === rosterEntry?.id || c.subject_callsign === rosterEntry?.callsign)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.id, rosterEntry?.id]);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!items.length) return <div className="text-center py-16 text-muted-foreground font-sans text-sm">No conduct reports on your record.</div>;
  const SEV: Record<string, string> = {
    low:      "text-yellow-400 border-yellow-500/30 bg-yellow-500/10",
    medium:   "text-orange-400 border-orange-500/30 bg-orange-500/10",
    high:     "text-red-400 border-red-500/30 bg-red-500/10",
    critical: "text-red-600 border-red-700/30 bg-red-700/10",
  };
  return (
    <div className="space-y-3">
      {items.map((c: any) => (
        <div key={c.id} className="border border-border/60 rounded-lg p-4">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${SEV[c.severity] ?? ""}`}>{c.severity}</span>
            <span className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">{c.category}</span>
            <span className="text-[10px] font-sans text-muted-foreground ml-auto">{new Date(c.report_date).toLocaleDateString()}</span>
          </div>
          <p className="text-sm font-sans text-foreground">{c.description}</p>
          {c.outcome && <p className="text-xs font-sans text-muted-foreground mt-2"><span className="font-bold text-foreground">Outcome:</span> {c.outcome}</p>}
          {c.outcome_notes && <p className="text-xs font-sans text-muted-foreground">{c.outcome_notes}</p>}
        </div>
      ))}
    </div>
  );
}

// ── MemberTrainingLibTab ──────────────────────────────────────────────────────
function MemberTrainingLibTab({ group }: any) {
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all"|"sop"|"training"|"roe">("all");
  useEffect(() => {
    if (!group?.id) return;
    apiFetch<any[]>(`/trainingDocs?path=group/${group.id}`)
      .then(d => setDocs((d ?? []).filter((x: any) => x.is_current !== false)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.id]);
  const FILTERS = [
    { id: "all",      label: "All" },
    { id: "sop",      label: "SOPs" },
    { id: "training", label: "Training" },
    { id: "roe",      label: "ROE" },
  ];
  const filtered = filter === "all" ? docs : docs.filter((d: any) => d.doc_type === filter);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  return (
    <div>
      <div className="flex gap-1.5 mb-5 flex-wrap">
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id as any)}
            className={`px-3 py-1.5 rounded border text-[10px] font-display font-bold uppercase tracking-widest transition-all ${
              filter === f.id ? "bg-primary/15 text-primary border-primary/40" : "bg-secondary border-border text-muted-foreground hover:text-foreground"
            }`}>{f.label}</button>
        ))}
        <span className="ml-auto text-[10px] font-sans text-muted-foreground self-center">{filtered.length} doc{filtered.length !== 1 ? "s" : ""}</span>
      </div>
      {!filtered.length ? (
        <div className="text-center py-16 text-muted-foreground font-sans text-sm">No documents in this category.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((d: any) => (
            <div key={d.id} className="flex items-center gap-3 p-4 border border-border/60 rounded-lg hover:border-primary/30 transition-all">
              <BookOpen className="w-4 h-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm uppercase tracking-wider truncate">{d.title}</div>
                <div className="text-xs text-muted-foreground font-sans mt-0.5">{d.doc_type?.toUpperCase()} {d.page_count ? `· ${d.page_count} pages` : ""} {d.ai_summary ? "· AI reviewed" : ""}</div>
              </div>
              {d.file_url && (
                <a href={d.file_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                  className="flex items-center gap-1 text-[10px] font-display uppercase tracking-widest text-primary hover:text-primary/80 shrink-0">
                  <ExternalLink className="w-3 h-3" /> View
                </a>
              )}
              {d.ai_score != null && (
                <span className={`text-[10px] font-display font-bold px-2 py-0.5 rounded border shrink-0 ${d.ai_score >= 7 ? "text-green-400 border-green-500/30 bg-green-500/10" : d.ai_score >= 4 ? "text-yellow-400 border-yellow-500/30 bg-yellow-500/10" : "text-red-400 border-red-500/30 bg-red-500/10"}`}>
                  {d.ai_score}/10
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MemberOrdersInbox ─────────────────────────────────────────────────────────
function MemberOrdersInbox({ group, user, rosterEntry }: any) {
  const [items, setItems]   = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [acking, setAcking] = useState(false);

  const roleId = rosterEntry?.role_id  ?? "";
  const rankId = rosterEntry?.rank_id  ?? "";
  const callsign = rosterEntry?.callsign ?? (user as any)?.username ?? "";

  useEffect(() => {
    if (!group?.id) return;
    apiFetch<any[]>(`/milsimOrderMemos?path=member/${group.id}/${roleId}/${rankId}`)
      .then(d => setItems(d ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.id, roleId, rankId]);

  const handleAck = async (item: any) => {
    if (!callsign) return;
    setAcking(true);
    const already = Array.isArray(item.acknowledged_by) ? item.acknowledged_by : [];
    if (already.includes(callsign)) { setAcking(false); return; }
    const updated = [...already, callsign];
    try {
      await apiFetch(`/milsimOrderMemos?path=${item.id}`, {
        method: "PATCH",
        body: JSON.stringify({ acknowledged_by: updated }),
      });
      setSelected((prev: any) => prev ? { ...prev, acknowledged_by: updated } : prev);
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, acknowledged_by: updated } : i));
    } catch {}
    setAcking(false);
  };

  const PRIORITY_COL: Record<string, string> = {
    routine:   "text-muted-foreground border-border bg-secondary/30",
    priority:  "text-blue-400 border-blue-500/30 bg-blue-500/10",
    immediate: "text-orange-400 border-orange-500/30 bg-orange-500/10",
    flash:     "text-red-400 border-red-500/30 bg-red-500/10",
  };
  const CL: Record<string, string> = {
    unclassified: "text-green-400 border-green-500/30 bg-green-500/10",
    confidential: "text-blue-400 border-blue-500/30 bg-blue-500/10",
    classified:   "text-yellow-400 border-yellow-500/30 bg-yellow-500/10",
    "top-secret": "text-red-400 border-red-500/30 bg-red-500/10",
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (selected) {
    const alreadyAcked = Array.isArray(selected.acknowledged_by) && selected.acknowledged_by.includes(callsign);
    return (
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border/40 bg-secondary/20 flex-wrap">
          <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-[10px] font-display uppercase tracking-widest text-muted-foreground hover:text-foreground mr-2">
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${PRIORITY_COL[selected.priority]}`}>{(selected.priority ?? "").toUpperCase()}</span>
          <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${CL[selected.classification]}`}>{selected.classification}</span>
          <span className="font-display font-black text-sm uppercase tracking-wider flex-1 truncate">{selected.type}: {selected.subject}</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-3 text-[10px] font-display uppercase tracking-widest text-muted-foreground">
            <span>From: <span className="text-foreground font-bold">{selected.author_callsign}</span></span>
            <span>{new Date(selected.created_date).toLocaleDateString()}</span>
            {selected.requires_ack && selected.ack_by && <span className="text-amber-400">Ack by: {selected.ack_by}</span>}
          </div>
          <p className="text-sm font-sans text-foreground whitespace-pre-wrap leading-relaxed">{selected.body}</p>
          {selected.requires_ack && (
            <div className="pt-3 border-t border-border/40">
              {alreadyAcked ? (
                <div className="flex items-center gap-2 text-green-400 text-sm font-display font-bold uppercase tracking-widest">
                  <CheckCircle2 className="w-4 h-4" /> Acknowledged
                </div>
              ) : (
                <button onClick={() => handleAck(selected)} disabled={acking}
                  className="flex items-center gap-2 px-4 py-2 rounded bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-display font-bold uppercase tracking-widest transition-all disabled:opacity-50">
                  {acking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckSquare className="w-3.5 h-3.5" />}
                  Acknowledge Order
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!items.length) return (
    <div className="text-center py-16 text-muted-foreground">
      <ScrollText className="w-8 h-8 mx-auto mb-3 opacity-20" />
      <p className="text-sm">No orders or memos issued</p>
      <p className="text-xs mt-1 text-muted-foreground/70">Your commander will issue orders here</p>
    </div>
  );

  // Sort: flash/immediate first, then by date desc
  const PRIO_ORDER: Record<string, number> = { flash: 0, immediate: 1, priority: 2, routine: 3 };
  const sorted = [...items].sort((a, b) => (PRIO_ORDER[a.priority] ?? 3) - (PRIO_ORDER[b.priority] ?? 3) || new Date(b.created_date).getTime() - new Date(a.created_date).getTime());

  return (
    <div className="space-y-2">
      {sorted.map((item: any) => {
        const acked = Array.isArray(item.acknowledged_by) && item.acknowledged_by.includes(callsign);
        return (
          <div key={item.id} onClick={() => setSelected(item)}
            className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all hover:bg-secondary/30 ${item.priority === "flash" ? "border-red-500/40 bg-red-500/5 hover:border-red-400/60" : item.priority === "immediate" ? "border-orange-500/30 bg-orange-500/5" : "border-border/60"}`}>
            <ScrollText className={`w-4 h-4 shrink-0 ${item.priority === "flash" ? "text-red-400" : item.priority === "immediate" ? "text-orange-400" : "text-primary"}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${PRIORITY_COL[item.priority]}`}>{(item.priority ?? "").toUpperCase()}</span>
                <span className="font-display font-bold text-sm uppercase tracking-wider truncate">{item.type}: {item.subject}</span>
              </div>
              <div className="text-[10px] text-muted-foreground font-sans mt-0.5">
                From {item.author_callsign} · {new Date(item.created_date).toLocaleDateString()}
                {item.requires_ack && !acked && <span className="text-amber-400 ml-2">⚠ Ack required</span>}
                {item.requires_ack && acked && <span className="text-green-400 ml-2">✓ Acknowledged</span>}
              </div>
            </div>
            <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border shrink-0 ${CL[item.classification]}`}>{item.classification}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MemberOpPlansTab — read-only view of Op Planning Room documents
// ─────────────────────────────────────────────────────────────────────────────
const OP_PHASE_LABELS: Record<string, string> = {
  planning:    "PLANNING",
  admin_log:   "ADMIN / LOG",
  rehearsal:   "REHEARSAL",
  infil:       "INFIL",
  action:      "ACTION",
  exfil:       "EXFIL",
  consolidation: "CONSOLIDATION",
};

function MemberOpPlansTab({ group }: { group: any }) {
  const [ops, setOps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!group?.id) return;
    apiFetch<any[]>(`/milsimOps?path=${group.id}/list`)
      .then((res: any) => {
        const list = Array.isArray(res) ? res : res?.ops ?? [];
        const withPlans = list.filter((op: any) => {
          try {
            const d = JSON.parse(op.description ?? "{}");
            return d.__planFields && Object.keys(d.__planFields).length > 0;
          } catch { return false; }
        });
        setOps(withPlans);
      })
      .catch(() => setOps([]))
      .finally(() => setLoading(false));
  }, [group?.id]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (!ops.length) return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg text-muted-foreground">
      <Layers className="w-8 h-8 mx-auto mb-3 opacity-30" />
      <p className="font-display text-sm uppercase tracking-widest">No Op Plans Published</p>
      <p className="text-xs mt-1 font-sans">HQ will publish Op Planning Room documents here before operations</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display font-bold uppercase tracking-widest text-sm flex items-center gap-2">
          <Layers className="w-4 h-4 text-primary" /> Op Planning Room — Published Plans
        </h3>
        <p className="text-xs text-muted-foreground font-sans mt-0.5">
          Read-only. All filled planning fields published by HQ for each operation.
        </p>
      </div>
      {ops.map((op: any) => {
        let planData: any = {};
        try { planData = JSON.parse(op.description ?? "{}"); } catch {}
        const fields = planData.__planFields ?? {};
        const chat: any[] = planData.__planChat ?? [];
        const isOpen = expanded === op.id;

        // Group by phase (key format: "phase::section::field")
        const grouped: Record<string, { section: string; label: string; value: string }[]> = {};
        Object.entries(fields).forEach(([k, v]: any) => {
          if (!v?.toString().trim()) return;
          const parts = k.split("::");
          const phase = parts[0] ?? "other";
          if (!grouped[phase]) grouped[phase] = [];
          grouped[phase].push({ section: parts[1] ?? "", label: parts[2] ?? k, value: v });
        });

        const phaseKeys = Object.keys(grouped);
        const filledCount = Object.values(fields).filter((v: any) => v?.toString().trim()).length;

        return (
          <div key={op.id} className="rounded-lg border border-border bg-secondary/10 overflow-hidden">
            <button
              onClick={() => setExpanded(isOpen ? null : op.id)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-secondary/20 transition-colors"
            >
              <div className="flex items-center gap-3">
                {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground -rotate-90" />}
                <div>
                  <p className="text-xs font-display font-bold uppercase tracking-widest text-foreground">{op.name}</p>
                  <p className="text-[10px] text-muted-foreground font-mono">
                    {op.scheduled_at ? new Date(op.scheduled_at).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" }) : "Date TBC"}
                    {" · "}{phaseKeys.length} phases · {filledCount} fields filled
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border border-primary/30 text-primary bg-primary/10">
                READ ONLY
              </span>
            </button>

            {isOpen && (
              <div className="divide-y divide-border/40 border-t border-border">
                {phaseKeys.map(phase => (
                  <div key={phase} className="px-4 py-3">
                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5">
                      <Flag className="w-3 h-3" />
                      {OP_PHASE_LABELS[phase] ?? (phase ?? "").toUpperCase()}
                    </p>
                    <div className="space-y-2">
                      {grouped[phase].map((e, i) => (
                        <div key={i}>
                          <p className="text-[10px] text-muted-foreground font-sans uppercase tracking-wide">
                            {e.section.replace(/_/g," ")} — {e.label.replace(/_/g," ")}
                          </p>
                          <p className="text-sm font-sans text-foreground whitespace-pre-wrap">{e.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {chat.length > 0 && (
                  <div className="px-4 py-3">
                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> Planning Discussion ({chat.length} messages)
                    </p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {chat.map((m: any) => (
                        <div key={m.id} className="flex gap-2 items-start">
                          <span className="text-[10px] font-display font-bold text-primary shrink-0">{m.callsign}</span>
                          <span className="text-xs font-sans text-foreground/80">{m.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MemberCommsPlansTab — read-only Comms Plans (saved from CommsPlanGenerator)
// ─────────────────────────────────────────────────────────────────────────────
function MemberCommsPlansTab({ group }: { group: any }) {
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!group?.id) return;
    apiFetch<any>(`/milsimBriefings?path=list&group_id=${group.id}`)
      .then((res: any) => {
        const list: any[] = normaliseBriefings(res);
        // Comms Plans are stored as briefings with title starting "COMMS PLAN:"
        setPlans(list.filter((b: any) => b.title?.startsWith("COMMS PLAN:")));
      })
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, [group?.id]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (!plans.length) return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg text-muted-foreground">
      <PhoneCall className="w-8 h-8 mx-auto mb-3 opacity-30" />
      <p className="font-display text-sm uppercase tracking-widest">No Comms Plans Published</p>
      <p className="text-xs mt-1 font-sans">HQ will publish comms plans here before operations</p>
    </div>
  );

  const SIGNAL_LABELS: Record<string, string> = {
    primary:   "Primary Net",
    alternate: "Alternate Net",
    contingency: "Contingency Net",
    emergency: "Emergency Net",
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display font-bold uppercase tracking-widest text-sm flex items-center gap-2">
          <PhoneCall className="w-4 h-4 text-primary" /> Comms Plans
        </h3>
        <p className="text-xs text-muted-foreground font-sans mt-0.5">
          Read-only. Comms plans published by HQ — channels, frequencies, and signal protocol.
        </p>
      </div>

      {plans.map((b: any) => {
        let commsData: any = {};
        try { commsData = JSON.parse(b.content ?? "{}"); } catch {}
        const isOpen = expanded === b.id;
        const opName = b.title.replace(/^COMMS PLAN:\s*/i, "");

        const nets = ["primary","alternate","contingency","emergency"].filter(k => commsData[k]?.channel);
        const hasCallsigns = (commsData.callsigns ?? []).length > 0;
        const hasProword = commsData.proword_brevity?.trim();
        const hasFreqHop = commsData.freq_hop_key?.trim();

        return (
          <div key={b.id} className="rounded-lg border border-border bg-secondary/10 overflow-hidden">
            <button
              onClick={() => setExpanded(isOpen ? null : b.id)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-secondary/20 transition-colors"
            >
              <div className="flex items-center gap-3">
                {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground -rotate-90" />}
                <div>
                  <p className="text-xs font-display font-bold uppercase tracking-widest text-foreground">{opName}</p>
                  <p className="text-[10px] text-muted-foreground font-mono">
                    {b.op_date ? new Date(b.op_date).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" }) : "Date TBC"}
                    {nets.length > 0 && ` · ${nets.length} nets configured`}
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border border-primary/30 text-primary bg-primary/10">
                READ ONLY
              </span>
            </button>

            {isOpen && (
              <div className="divide-y divide-border/40 border-t border-border">
                {/* Nets */}
                {nets.length > 0 && (
                  <div className="px-4 py-3">
                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5">
                      <Antenna className="w-3 h-3" /> Signal Nets
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {nets.map(netKey => {
                        const net = commsData[netKey] ?? {};
                        return (
                          <div key={netKey} className="rounded border border-border/60 bg-background/40 p-3 space-y-1">
                            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">{SIGNAL_LABELS[netKey]}</p>
                            {net.channel   && <p className="text-sm font-sans"><span className="text-muted-foreground text-xs">CH: </span>{net.channel}</p>}
                            {net.frequency && <p className="text-sm font-sans"><span className="text-muted-foreground text-xs">Freq: </span>{net.frequency}</p>}
                            {net.platform  && <p className="text-sm font-sans"><span className="text-muted-foreground text-xs">Platform: </span>{net.platform}</p>}
                            {net.notes     && <p className="text-xs text-muted-foreground font-sans">{net.notes}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Callsigns */}
                {hasCallsigns && (
                  <div className="px-4 py-3">
                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary mb-2">Callsign List</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {(commsData.callsigns ?? []).map((c: any, i: number) => (
                        <div key={i} className="flex items-center gap-2 text-xs font-sans">
                          <span className="font-display font-bold text-foreground">{c.callsign}</span>
                          {c.role && <span className="text-muted-foreground">{c.role}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Brevity / Freq hop */}
                {(hasProword || hasFreqHop) && (
                  <div className="px-4 py-3 space-y-2">
                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary mb-1">Signal Protocol</p>
                    {hasProword && (
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase">Brevity / Prowords</p>
                        <p className="text-sm font-sans text-foreground whitespace-pre-wrap">{commsData.proword_brevity}</p>
                      </div>
                    )}
                    {hasFreqHop && (
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase">Freq Hop Key</p>
                        <p className="text-sm font-sans text-foreground">{commsData.freq_hop_key}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Raw comms_plan text (briefing-style) */}
                {b.comms_plan && !commsData.primary && (
                  <div className="px-4 py-3">
                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Comms Details</p>
                    <p className="text-sm font-sans text-foreground whitespace-pre-wrap">{b.comms_plan}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
